import { useEffect, useState, Fragment, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { REACT_APP_COUNTRY } from '../../envVars';
import {
  Card,
  HeaderStyle,
  Tag,
  TagStyle,
  Theme,
  IconStyle,
  ButtonStyles,
  // ButtonIconPosition,
  ButtonSize,
  Modal,
  Checkbox,
  TextAlign,
  Button,
  ButtonTypes,
  InputTypes,
  MLPVersion,
  Input,
  // SearchComponent,
  DetailCardProgress,
  useBreakpoint,
  Tooltip,
  TooltipPreference,
  DatePicker,
  ButtonWidth,
  Drawer,
  ButtonType,
  IconButtonProps,
  FAIcon,
  EnrollmentType,
  DropDown,
  ProfileImage,
} from '../../ui/components';
import { DropDownData } from '../../ui/components/Form/DropDown/types/propsType';
import {
  dateTimeToCustomFormat,
  formatDateDMYYYY,
  LoggerService,
  PAGES,
  trackPage,
  decryptString,
  processBeforeAfterImagesData,
  encryptString,
  getTranslatedMessage,
  convertGMT8date,
  getEnrollmentType,
  getTeamStatus,
  getRewardStatus,
  validateAttachment,
  computeMemberProgress,
  isMemberLocked,
  TeamProgressStep,
  resolveProfileImageSectionShown,
  resolveGenderAgeThreshold,
} from '../../ui/utils';
import {
  Table as TableV2,
  TableVersion,
  TextLink,
  TextLinkStyle,
  IconStyle as IconStyleV2,
} from '../../ui/componentsV2';
import type { ColumnsInterface } from '../../ui/componentsV2/Table';
import { Theme as ThemeV2, MLPVersion as MLPVersionV2 } from '../../ui/componentsV2/types/enums';
import VideoEditor from '../../ui/components/VideoEditor/VideoEditor';
import BeforeAfterSlider from '../../ui/app-components/BeforeAfterSlider/BeforeAfterSlider';
import { useAdminToast } from '../../ui/components/AdminToast/AdminToast';
//import DetailCardProgress from '../../ui/components/DetailCardProgress/DetailCardProgress';
import OperationCenterErrorCard, {
  ErrorType,
} from '../../ui/components/OperationCenterErrorCard/OperationCenterErrorCard';
import { getRoundedNumber, getRoundedNumber2 } from '../../ui/utils/common';
import type { DynamicObjectInterface, AttachmentImageBA } from '../../sdk/type';
import CoachService from '../../sdk/services/coachService';
import ParticipantService from '../../sdk/services/participantService';
import LeaderService from '../../sdk/services/leaderService';
// import TeamService from '../../sdk/services/teamService';
import CampaignService from '../../sdk/services/campaignService';
import { useAPICall } from '../../hooks';
import CirclePlusGreen from '../../assets/image/icons/circle-plus-green.svg';
import WarningIcon from '../../assets/image/icons/warning.svg';
import PhotoUnavailable from '../../assets/image/sample/photoUnavailable.png';
import CampaignLogo from '../../assets/image/Campaign_Logo.png';
//import LayerSquarePlus from '../../assets/image/icons/layer_square_plus.svg';
import healthGoalService from '../../sdk/services/healthGoalService';
import { customEventPropType } from '../../ui/components/ImageEditor/types/propsType';
import AttachmentService from '../../sdk/services/attachmentService';
import { ActionType } from '../../ui/components/types/enums';
import { InputValidations } from '../../ui/components/Form/SearchComponent/types/propTypes';
import TableWithAction from '../../ui/components/TableWithAction/TableWithAction';
//import { MenuItem } from '../../ui/components/Menu/Menu';
import { ContentItem } from '../terms/Terms';
import { getContent } from '../../sdk/services/contentService';
import intermediateMeasurementService from '../../sdk/services/intermediateMeasurementService';
import Error from '../../assets/image/icons/ico_error_orange.svg';
import { UserData, DashboardProps, ActivityTypeOrder } from './dashboardConfig';
import styles from './Dashboard.module.scss';
import { ResultModal } from './editModals';

interface ActivityValueAmount {
  amount: number;
  currency: string;
}

interface ActivityRule {
  type: string;
  operator: string;
  value: ActivityValueAmount | string[][] | { base: string[][]; addOn: string[][] };
  combine: string;
}

interface Activities {
  maxOrderNumber: number;
  orderPeriod: number;
  rules: ActivityRule[];
}
export interface CampaignConfigDataOrder {
  recId: number;
  campaignId: string;
  campaignName: string;
  campaignDescription: string;
  campaignStart: string;
  campaignEnd: string;
  campaignStatus: string;
  campaignTextLink: string;
  campaignTextLinkDisplay: number;
  activities: Activities;
  configJson: DynamicObjectInterface;
}
export interface ProgressType {
  weighingDate: Date;
  weight: number;
  checked: boolean;
}

export interface CampaignQuestionType {
  questionRecId: number;
  questionText: string;
  questionType: string;
  options: null;
}

export interface MasterRewardType {
  rewardCode: string;
  rewardName: string;
  imageURL: string;
  [x: string]: DynamicObjectInterface;
}

export interface Option {
  title: string;
  key: string;
}

export interface UserAnswerDataType {
  answerId: number;
  questionType: string;
  answerText: string;
  options: Option[] | string;
}

interface FileWithUrl {
  file: File;
  url: string;
  photoId?: number;
}
interface ProgressItem {
  file: FileWithUrl;
  percentage: number;
}

type SectionState = {
  // eslint-disable-next-line no-unused-vars
  [key in (typeof Sections)[keyof typeof Sections]]: boolean;
};

const Sections = {
  Result: 'weightDetails',
} as const;

export const Dashboard = (props: DashboardProps) => {
  const { index, campaignData, decodedData, viewUserId, viewTeamId, readOnly = false } = props;
  // The participant this dashboard is showing: the viewed member in read-only mode,
  // otherwise the logged-in user.
  const targetUserId = viewUserId ?? decodedData.userId;
  // Team the user picked when navigating here (from the home table / My Team card).
  // Without it, My Progress always defaulted to the first team in the dropdown.
  const [searchParams] = useSearchParams();
  const preselectedTeamId = searchParams.get('tId') ?? undefined;
  const { t } = useTranslation();
  const [data, setData] = useState<UserData | null>(null);
  const [formData, setFormData] = useState<UserData | null>(JSON.parse(JSON.stringify(data ?? '')) as UserData);
  // const [alpValue, setAlpValue] = useState<{ input: string; check: boolean }>({ input: '', check: false });
  // const [showConfirmation, setShowConfirmation] = useState(false);
  // const [showCoachList, setShowCoachList] = useState(false);
  // const [masterList, setMasterList] = useState<{
  //   coachList: DynamicObjectInterface;
  // }>();
  // const [filteredCoachList, setFilteredCoachList] = useState(masterList?.coachList);
  const [isParticipant, setIsParticipant] = useState(false);
  const [isCampaignEnd, setIsCampaignEnd] = useState(false);
  const [campaignDetail, setCampaignDetail] = useState<CampaignConfigDataOrder>();
  const [isCoach, setIsCoach] = useState(false);
  //const [isLeader, setIsLeader] = useState(false);
  const [images, setImages] = useState<Record<string, AttachmentImageBA>>({
    before_front: { photoId: 0, url: '', file: null },
    after_front: { photoId: 0, url: '', file: null },
    before_side: { photoId: 0, url: '', file: null },
    after_side: { photoId: 0, url: '', file: null },
  });
  // Profile photo of the member being viewed (read-only view of another member).
  const [memberProfileImageUrl, setMemberProfileImageUrl] = useState<string | undefined>(undefined);
  const [showEdit, setShowEdit] = useState<SectionState>(
    Object.fromEntries(Object.values(Sections).map(section => [section, false])) as SectionState,
  );
  // Which tab the assessment (Result) modal should open on: 'weightIn' (pre) or
  // 'weightOut' (post). Set by the Upload Pre/Post-Assessment actions.
  const [resultInitialTab, setResultInitialTab] = useState<'weightIn' | 'weightOut'>('weightIn');
  const [files, setFiles] = useState<{ [key: string]: FileWithUrl[] }>({});
  const [progress, setProgress] = useState<{ [key: string]: ProgressItem[] }>({});
  // Uploaded check-in proof files keyed by day: { photoId, fileName, type } for the
  // download link in the check-in table. Populated by fetchMeasurementImage.
  const [checkInFiles, setCheckInFiles] = useState<{
    [day: number]: { photoId: number; fileName: string; type?: string }[];
  }>({});
  // Check-in submission modal: the day being submitted (null = closed) + remarks.
  const [checkInDay, setCheckInDay] = useState<number | null>(null);
  const [checkInRemarks, setCheckInRemarks] = useState('');
  const [checkInSubmitting, setCheckInSubmitting] = useState(false);
  const apiCall = useAPICall();
  const { adminToast } = useAdminToast();
  //const navigate = useNavigate();
  const location = useLocation();
  const [userAnswerData, setUserAnswerData] = useState<UserAnswerDataType>();
  const [teams, setTeams] = useState<DynamicObjectInterface[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');

  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm'].indexOf(viewPortType) === -1;
  const currentDate = new Date();

  const fetchData = async (teamIdArg?: string) => {
    let isParticipant = campaignData[index].enrollment?.some(enrollment => enrollment.enrollmentType === 'PARTICIPANT');
    let isCoach = campaignData[index].enrollment?.some(enrollment => enrollment.enrollmentType === 'COACH');
    let isLeader = campaignData[index].enrollment?.some(enrollment => enrollment.enrollmentType === 'LEADER');

    const participantTeamId =
      teamIdArg ??
      (viewTeamId != null ? `${viewTeamId}` : undefined) ??
      selectedTeamId ??
      campaignData[index].enrollment.find(enrollment => enrollment.enrollmentType === 'PARTICIPANT')?.teamId;

    // In read-only member-view mode the viewed user is always a participant.
    if (viewUserId != null) {
      isParticipant = true;
    }

    let _data: UserData | null = null;

    if (isParticipant) {
      let res = await apiCall(
        ParticipantService.getParticipantDetail,
        {
          campaignId: campaignData[index].campaignId,
          teamId: participantTeamId,
        },
        targetUserId,
      );
      let teamData = await apiCall(ParticipantService.getTeamParticipants, {
        campaignId: campaignData[index].campaignId,
        teamId: participantTeamId,
      });
      const rawData = res?.data?.data;
      const modifiedData: DynamicObjectInterface = {
        ...rawData,
        weightIn: {
          ...rawData?.weightIn,
          otherIndicators: rawData?.weightIn?.otherIndicators
            ? Object.entries(rawData?.weightIn?.otherIndicators || {}).reduce(
                (acc, [key, value]) => {
                  acc[key] = value ? getRoundedNumber2(parseFloat(decryptString(value.toString()))) : null;
                  return acc;
                },
                {} as Record<string, number | null>,
              )
            : null,
        },
        weightOut: {
          ...rawData?.weightOut,
          //machine: 'A',
          otherIndicators: rawData?.weightOut?.otherIndicators
            ? Object.entries(rawData?.weightOut?.otherIndicators || {}).reduce(
                (acc, [key, value]) => {
                  acc[key] = value ? getRoundedNumber2(parseFloat(decryptString(value.toString()))) : null;
                  return acc;
                },
                {} as Record<string, number | null>,
              )
            : null,
        },
      };

      _data = { ...modifiedData };
      if (_data) {
        _data.activities = [];
        _data.activities.push({
          activityName: modifiedData?.rewards?.[0]?.activityName,
          reward: modifiedData?.rewards?.map((r: DynamicObjectInterface) => {
            return {
              ...r,
              rewardStatus: r.rewardStatus,
            };
          }),
          type: 'PARTICIPANT',
        });
        _data.campaignName = modifiedData?.campaign?.campaignName;
        _data.teamLeaderLoginId = modifiedData?.team?.teamLeaderLoginId;
        // Team leader name: prefer the explicit team leader (gt.teamLeader). When that
        // isn't set on the team row it comes back empty, so fall back to the team's
        // coach — in this app the coach is the team creator/leader (mirrors the
        // MyTeamMulti "Team Leader" derivation). Use `||` so empty strings fall
        // through, not just null/undefined.
        _data.teamLeader =
          modifiedData?.team?.teamLeaderName ||
          teamData?.data?.data?.[0]?.teamDetail?.teamLeader ||
          modifiedData?.coachName ||
          teamData?.data?.data?.[0]?.teamDetail?.coachName ||
          '';
        _data.teamLeaderUserId = modifiedData?.team?.teamLeaderUserId;
        _data.teamStartDate = teamData?.data?.data?.[0]?.teamDetail?.teamStartDate;
        _data.teamEndDate = teamData?.data?.data?.[0]?.teamDetail?.teamEndDate;
        _data.participantDetails = teamData?.data?.data;
        _data.teamName = teamData?.data?.data?.[0]?.teamDetail?.teamName;
        _data.teamId = participantTeamId;
        _data.subChallengeId =
          teamData?.data?.data?.find((p: DynamicObjectInterface) => p.userId === modifiedData?.userId)?.subChallengeId ??
          teamData?.data?.data?.[0]?.subChallengeId ??
          modifiedData?.subChallengeId;
        _data.teamSize = teamData?.data?.data?.[0]?.teamDetail?.teamSize;
        _data.teamIncomplete =
          teamData?.data?.data?.[0]?.teamDetail?.participationType === 'ind'
            ? false
            : teamData?.data?.data.filter((participant: DynamicObjectInterface) => participant.status === 'ACTIVE')
                .length < Number(teamData?.data?.data?.[0]?.teamDetail?.teamSize);
        //team coach
        _data.teamCoachId = teamData?.data?.data?.[0]?.teamDetail?.coachId;
        _data.teamCoachName = teamData?.data?.data?.[0]?.teamDetail?.coachName;
        // team status
        _data.teamStatus = teamData?.data?.data?.[0]?.status;
        _data.teamDescription = teamData?.data?.data?.[0]?.teamDetail?.teamDescription;
        _data.teamPoints = teamData?.data?.data
          ?.filter((participant: DynamicObjectInterface) => participant.status === 'ACTIVE')
          ?.map((participant: DynamicObjectInterface) => participant.point)
          ?.flatMap((item: DynamicObjectInterface) => Object.values(item))
          ?.reduce((acc: number, value: number) => acc + value, 0);
        _data.participationType = teamData?.data?.data?.[0]?.teamDetail?.participationType;
        // _data.teamLeaderLoginId = teamData?.data?.data?.[0]?.teamDetail?.teamLeaderLoginId;
      }
      setFormData((i: DynamicObjectInterface) => {
        i = { ...modifiedData };
        i.subscribe = modifiedData.subscribe;
        i.teamIncomplete =
          teamData?.data?.data?.[0]?.teamDetail?.participationType === 'ind'
            ? false
            : teamData?.data?.data.filter((participant: DynamicObjectInterface) => participant.status === 'ACTIVE')
                .length < Number(teamData?.data?.data?.[0]?.teamDetail?.teamSize);
        return { ...i };
      });
    }

    if (isCoach) {
      let res = await apiCall(
        CoachService?.getCoachDetailByCampaignId,
        (
          campaignData[index].enrollment.find(enrollment => enrollment.enrollmentType === 'COACH')?.enrollmentId ?? ''
        ).toString(),
      );
      let coachData = res?.data?.data?.[0];
      if (!isParticipant) {
        _data = coachData;
      }

      if (_data) {
        if (!_data?.activities) _data.activities = [];
        _data.achievement = coachData?.achievement;
        _data.coachId = coachData?.coachId;
        _data.campaignName = coachData?.campaignName;
        _data.coachEnrollmentDate = coachData?.enrollmentDate;
        _data.coachEndDate = coachData?.endDate;
        _data.directLeaderName = coachData?.directLeaderName;
        _data.directLeader = coachData?.directLeader;

        if (!isLeader) {
          _data.cumulativePoint = coachData?.cumulativePoint;
          _data.newAboCount = coachData?.newAboCount;
          _data.newGroupAboCount = coachData?.newGroupAboCount;
          if (coachData?.rewards?.filter((i: DynamicObjectInterface) => i.rewardStatus === 'ACTIVE').length > 0) {
            _data.activities?.push({
              activityName: coachData?.rewards?.[0]?.activityName,
              reward: coachData?.rewards?.filter((i: DynamicObjectInterface) => i.rewardStatus === 'ACTIVE'),
              type: 'COACH',
            });
          }
        }
      }
    }

    if (isLeader) {
      let res = await apiCall(
        LeaderService.getLeaderDetailsByRecId,
        (
          campaignData[index].enrollment.find(enrollment => enrollment.enrollmentType === 'LEADER')?.enrollmentId ?? ''
        ).toString(),
      );
      let leaderData = res?.data?.data;
      if (!isParticipant && !isCoach) {
        _data = leaderData;
      }

      if (_data) {
        _data.totalAboReqruitment = leaderData?.totalAboReqruitment;
        _data.totalQualified = leaderData?.totalQualified;
        _data.totalPoint = leaderData?.totalPoint;
        _data.newGroupAboCount = leaderData?.newGroupAboCount;

        if (!_data?.activities) _data.activities = [];
        _data.activities?.push({
          activityName: leaderData?.rewards?.[0]?.activityName,
          reward: leaderData?.rewards?.filter((i: DynamicObjectInterface) => i.rewardStatus === 'ACTIVE'),
          type: 'LEADER',
        });

        if (isCoach) {
          _data.activities = _data?.activities?.filter(activity => activity.type !== 'COACH') || [];
        }
      }
    }

    const typeOrder: ActivityTypeOrder = { COACH: 0, LEADER: 1, PARTICIPANT: 2 };

    // Reorder activities, COACH/LEADER rewards followed by PARTICIPANT rewards
    _data?.activities?.sort((a, b) => {
      return typeOrder[a.type] - typeOrder[b.type];
    });
    setData(_data);

    let getCampaignDetail = await apiCall(
      CampaignService.getCampaignDetailByCampaignId,
      campaignData[index].campaignId,
    );
    setCampaignDetail(getCampaignDetail.data.data);
    setIsCampaignEnd(
      getCampaignDetail.data.data ? new Date(getCampaignDetail.data.data.campaignEnd) < new Date() : false,
    );
  };

  useEffect(() => {
    setIsParticipant(false);
    setIsCoach(false);
    //setIsLeader(false);
    campaignData[index].enrollment.forEach(enrollment => {
      switch (enrollment?.enrollmentType) {
        case 'PARTICIPANT':
          setIsParticipant(true);
          break;
        case 'COACH':
          setIsCoach(true);
          break;
        // case 'LEADER':
        //   setIsLeader(true);
        //   break;
      }
    });
    loadTeams();
    const timeout = setTimeout(() => {
      trackPage(PAGES.campaignDashboard);
    }, 1000);
    return () => {
      clearTimeout(timeout);
    };
  }, [campaignData[index]?.campaignId]);

  // Read-only member view: fetch the viewed member's profile photo for the header.
  useEffect(() => {
    if (!readOnly || !data?.userId) {
      setMemberProfileImageUrl(undefined);
      return;
    }
    (async () => {
      try {
        const response = await AttachmentService.downloadProfileImage(data.userId ?? '');
        setMemberProfileImageUrl(response?.data?.data?.image || undefined);
      } catch (error) {
        LoggerService.log('Error downloading member profile image', error);
        setMemberProfileImageUrl(undefined);
      }
    })();
  }, [readOnly, data?.userId]);

  const loadTeams = async () => {
    // Read-only member view: skip the logged-in user's team list and load the viewed
    // member's specific team directly. The dropdown is shown locked to this team.
    if (viewUserId != null) {
      fetchData(viewTeamId != null ? `${viewTeamId}` : undefined);
      return;
    }

    const isParticipant = campaignData[index].enrollment?.some(
      enrollment => enrollment.enrollmentType === 'PARTICIPANT',
    );
    const coachId = campaignData[index].enrollment?.find(
      (i: DynamicObjectInterface) => i.enrollmentType === 'COACH',
    )?.coachId;

    if (isParticipant) {
      const result = await apiCall(ParticipantService.getTeamListByUser, {
        campaignId: campaignData[index]?.campaignId,
        loginId: decodedData?.loginId,
        userId: decodedData?.userId,
        coachId,
      });

      // A dual-role user (coach + participant on the same team) gets the team's
      // re-derived enrollmentType collapsed to COACH, so don't trust it. Instead keep
      // any team where this user has a PARTICIPANT enrollment in the participants list.
      const idMatches = (a?: string | number, b?: string | number) =>
        a !== undefined && a !== null && b !== undefined && b !== null && `${a}` === `${b}`;
      const teamList: DynamicObjectInterface[] = (result?.data?.data ?? []).filter((team: DynamicObjectInterface) =>
        (team?.participants ?? []).some(
          (p: DynamicObjectInterface) =>
            idMatches(p.userId, decodedData?.userId) || idMatches(p.loginId, decodedData?.userId),
        ),
      );

      setTeams(teamList);
      // Prefer the team the user navigated in with (?tId=...), when it's one of the
      // user's teams; otherwise fall back to the first team.
      const preselectedInList = preselectedTeamId
        ? teamList.find((team: DynamicObjectInterface) => `${team.teamId}` === `${preselectedTeamId}`)
        : undefined;
      const defaultTeamId =
        preselectedInList?.teamId ??
        teamList?.[0]?.teamId ??
        campaignData[index].enrollment.find(enrollment => enrollment.enrollmentType === 'PARTICIPANT')?.teamId ??
        '';
      setSelectedTeamId(`${defaultTeamId}`);
      fetchData(`${defaultTeamId}`);
    } else {
      fetchData();
    }
  };

  const handleSelectTeam = (teamId: string) => {
    if (!teamId || teamId === selectedTeamId) return;
    setSelectedTeamId(teamId);
    fetchData(teamId);
  };

  const teamOptions: DropDownData[] = useMemo(() => {
    // Read-only member view has no team list loaded; show the viewed team only.
    if (viewUserId != null) {
      return data?.teamId ? [{ id: `${data.teamId}`, name: data?.teamName ?? '' }] : [];
    }
    return teams.map(team => ({ id: `${team.teamId}`, name: team.teamName }));
  }, [teams, viewUserId, data?.teamId, data?.teamName]);

  const selectedTeam = useMemo(() => {
    if (viewUserId != null) {
      // Build a team-shaped object from the fetched detail for progress derivation.
      return { teamId: data?.teamId, teamName: data?.teamName, participants: data?.participantDetails ?? [] };
    }
    return teams.find(team => `${team.teamId}` === `${selectedTeamId}`);
  }, [teams, selectedTeamId, viewUserId, data?.teamId, data?.teamName, data?.participantDetails]);

  const subChallenges: DynamicObjectInterface[] = data?.campaign?.configJson?.activities?.subChallenges ?? [];

  const activeSubKpiCode: DynamicObjectInterface[] = useMemo(() => {
    const configJson = data?.campaign?.configJson;
    if (configJson?.campaignMode === 'MULTI' && data?.subChallengeId) {
      const sc = subChallenges.find(
        (i: DynamicObjectInterface) => i.subChallengeId === data?.subChallengeId,
      );
      if (sc?.rules?.[4]?.subKpiCode) return sc.rules[4].subKpiCode;
    }
    return configJson?.activities?.participant?.rules?.[4]?.subKpiCode ?? [];
  }, [data?.campaign?.configJson, data?.subChallengeId]);

  // When the sub-challenge uses test devices, the assessment display shows only the
  // indicators for the participant's chosen device (stored on weightIn.machine).
  // Untagged indicators always show; no device configured => show everything.
  const displaySubKpiCode: DynamicObjectInterface[] = useMemo(() => {
    const device = data?.weightIn?.machine;
    const hasDeviceTagged = activeSubKpiCode?.some((i: DynamicObjectInterface) => !!i.testDevice);
    if (!hasDeviceTagged) return activeSubKpiCode;
    return activeSubKpiCode.filter((i: DynamicObjectInterface) => !i.testDevice || i.testDevice === device);
  }, [activeSubKpiCode, data?.weightIn?.machine]);

  // Test device chosen by the participant (stored as a device code on weightIn.machine,
  // e.g. "DEVICE_1781846982208"). Resolve to its configured display name; show the
  // Scan Type column only when a device is actually present.
  const selectedDeviceCode = data?.weightIn?.machine ?? data?.weightOut?.machine;
  const selectedDeviceName = useMemo(() => {
    if (!selectedDeviceCode) return '';
    const sc = subChallenges.find((s: DynamicObjectInterface) => s.subChallengeId === data?.subChallengeId);
    return (
      sc?.testDevices?.find((d: DynamicObjectInterface) => d.code === selectedDeviceCode)?.name ?? selectedDeviceCode
    );
  }, [selectedDeviceCode, subChallenges, data?.subChallengeId]);
  const showScanType = !!selectedDeviceName;

  // Signed diff value for the dedicated "Change" column (no brackets). Mirrors the
  // calculation in renderAssessmentDiff but returns a string + sign class.
  const getAssessmentChange = (
    i: DynamicObjectInterface,
  ): { text: string; achieved: boolean; winLose?: boolean } | null => {
    const weightInValue = data?.weightIn?.otherIndicators?.[i.subKpiCode];
    const weightOutValue = data?.weightOut?.otherIndicators?.[i.subKpiCode];

    // --- Gender/Age-threshold indicators (e.g. Grip Strength) ------------------
    // These have no meaningful pre/post numeric diff — the passing standard is an
    // absolute post value that varies by gender + age group (genderAgeThresholds
    // matrix). A plain diff produced NaN, so instead we judge win/lose against the
    // resolved threshold and render a tick/cross. Mirrors the BFF
    // resolveGenderAgeThreshold + checkIndividualAchieved logic.
    const genderAgeThreshold = resolveGenderAgeThreshold(i, data?.gender, data?.ageGroup);
    if (i?.indicatorWinningCriteria && genderAgeThreshold !== null) {
      if (weightOutValue === null || weightOutValue === undefined || !data?.weightOut?.weightedDate) return null;
      const measured = getRoundedNumber(weightOutValue);
      const achieved =
        i?.indicatorWinningCriteria === 'loss' ? measured <= genderAgeThreshold : measured >= genderAgeThreshold;
      return { text: '', achieved, winLose: true };
    }

    // --- Disable/Ignore pre-assessment indicators (e.g. Sit to Stand) ----------
    // Judged against the post value only (no pre), so a "difference" number is
    // misleading. Show win/lose vs the configured target instead.
    if (i?.disablePreAssessment === 1 || i?.ignorePreAssessment === 1) {
      if (weightOutValue === null || weightOutValue === undefined || !data?.weightOut?.weightedDate) return null;
      const target = parseFloat(i?.targetFrom);
      if (isNaN(target)) return null;
      const measured = getRoundedNumber(weightOutValue);
      const achieved =
        i?.indicatorWinningCriteria === 'loss' ? measured <= target : measured >= target;
      return { text: '', achieved, winLose: true };
    }

    // --- Standard diff indicators (pre/post difference or percentage) ----------
    // Need a post-assessment value (and date) to show a change. A zero change is
    // still rendered (e.g. +0).
    if (weightInValue === null || weightInValue === undefined) return null;
    if (weightOutValue === null || weightOutValue === undefined || !data?.weightOut?.weightedDate) return null;

    // Raw signed change shown to the user (display only).
    const diff =
      i?.percentageCalculation === 1
        ? getRoundedNumber(((weightOutValue - weightInValue) / weightInValue) * 100)
        : getRoundedNumber(weightOutValue - weightInValue);

    // Direction-normalised achievement, mirroring the backend checkIndividualAchieved
    // (loss flips the sign), then compared against targetFrom. Color only — does NOT
    // affect the backend winning calc or the report.
    const normalizedDiff =
      i?.percentageCalculation === 1
        ? i?.indicatorWinningCriteria === 'loss'
          ? getRoundedNumber(((weightInValue - weightOutValue) / weightInValue) * 100)
          : getRoundedNumber(((weightOutValue - weightInValue) / weightInValue) * 100)
        : i?.indicatorWinningCriteria === 'loss'
          ? weightInValue - weightOutValue
          : weightOutValue - weightInValue;

    const target = parseFloat(i?.targetFrom);
    const achieved = !isNaN(target) && normalizedDiff >= target;

    const suffix = i?.percentageCalculation === 1 ? '%' : '';
    return {
      text: `${diff > 0 ? '+' : ''}${diff.toFixed(1)}${suffix}`,
      achieved,
    };
  };

  // Check-in config for the selected sub-challenge ({ days, fileUploader }). Only MULTI
  // campaigns configure check-in; empty days => check-in step stays a grey placeholder.
  const checkInConfig = useMemo(() => {
    const configJson = data?.campaign?.configJson;
    if (configJson?.campaignMode === 'MULTI' && data?.subChallengeId) {
      const sc = subChallenges.find((i: DynamicObjectInterface) => i.subChallengeId === data?.subChallengeId);
      return {
        days: (sc?.checkIn?.days ?? []) as (number | string)[],
        fileUploader: (sc?.checkIn?.fileUploader ?? []) as DynamicObjectInterface[],
      };
    }
    return { days: [] as (number | string)[], fileUploader: [] as DynamicObjectInterface[] };
  }, [data?.campaign?.configJson, data?.subChallengeId]);

  const checkInDays = useMemo(
    () =>
      Array.from(new Set((checkInConfig.days ?? []).map(d => Number(d)).filter(d => !isNaN(d) && d > 0))).sort(
        (a, b) => a - b,
      ),
    [checkInConfig],
  );

  const currentMember = useMemo(() => {
    const matches = (a?: string | number, b?: string | number) =>
      a !== undefined && a !== null && b !== undefined && b !== null && `${a}` === `${b}`;
    const pickFrom = (members: DynamicObjectInterface[]) =>
      members.find(p => matches(p.userId, targetUserId) || matches(p.userId, data?.userId)) ??
      (viewUserId == null ? members.find(p => matches(p.loginId, decodedData?.loginId)) : undefined);
    // Prefer the freshly-refetched per-team detail list; fall back to the team-list payload.
    return (
      pickFrom((data?.participantDetails as DynamicObjectInterface[]) ?? []) ??
      pickFrom((selectedTeam?.participants as DynamicObjectInterface[]) ?? [])
    );
  }, [data?.participantDetails, data?.userId, selectedTeam, targetUserId, viewUserId, decodedData?.loginId]);

  const memberProgress = useMemo(
    () => computeMemberProgress(currentMember ?? {}, checkInDays),
    [currentMember, checkInDays],
  );

  // Locked = the team has not started. Use whichever source carries teamStartDate.
  const teamLocked = useMemo(
    () => isMemberLocked({ teamStartDate: data?.teamStartDate ?? selectedTeam?.teamStartDate }),
    [data?.teamStartDate, selectedTeam],
  );

  // ---- Check-In derived state ---------------------------------------------------
  const checkInConfigured = checkInDays.length > 0;
  const submittedCheckInDays = useMemo(
    () =>
      new Set(
        (Array.isArray(currentMember?.checkIns) ? currentMember.checkIns : [])
          .map((c: DynamicObjectInterface) => Number(c?.day))
          .filter((d: number) => !isNaN(d)),
      ),
    [currentMember],
  );

  // A check-in day's form opens on teamStartDate + (day - 1), but ONLY after the
  // pre-assessment is done. Check-in must not open ahead of pre-assessment even if
  // the configured day window has already arrived.
  const teamStartDate = data?.teamStartDate ?? selectedTeam?.teamStartDate;
  const dayIsOpen = (day: number): boolean => {
    if (!teamStartDate) return false;
    // Gate on pre-assessment completion (weightIn submitted).
    if (!memberProgress.isAllWeightIn) return false;
    const open = new Date(teamStartDate);
    open.setHours(0, 0, 0, 0);
    open.setDate(open.getDate() + (day - 1));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.getTime() >= open.getTime();
  };

  // The next check-in day the user can submit: open + not yet submitted (earliest first).
  const nextOpenCheckInDay: number | null =
    checkInDays.find(d => dayIsOpen(d) && !submittedCheckInDays.has(d)) ?? null;

  const isCheckInComplete = checkInConfigured && checkInDays.every(d => submittedCheckInDays.has(d));

  // Post-Assessment time gate — mirrors the read-only rule in AssessmentDetail
  // (assessmentDetail.tsx): the post-assessment form only opens once
  // today >= teamStartDate + (validity - postAssessmentReminderDays) days, and only
  // after pre-assessment is submitted. The timeline step + "Upload Post-Assessment"
  // button must use the same gate, otherwise the step shows "Unlocked"/the button is
  // active while the form is still read-only.
  const postAssessmentWindowOpen = useMemo(() => {
    const configJson = data?.campaign?.configJson;
    const validity = parseInt(configJson?.activities?.participant?.validity?.value);
    const reminderDays = parseInt(configJson?.postAssessmentReminderDays);
    // Pre-assessment must be done and the team must have started.
    if (!data?.weightIn?.weightedDate || !teamStartDate) return false;
    // If validity isn't configured we can't compute the window; fall back to open
    // (matches AssessmentDetail, which only gates when teamStartDate + validity exist).
    if (isNaN(validity)) return true;
    const openDate = new Date(
      new Date(teamStartDate).getTime() + (validity - (isNaN(reminderDays) ? 0 : reminderDays)) * 24 * 60 * 60 * 1000,
    );
    return new Date() >= openDate;
  }, [data?.campaign?.configJson, data?.weightIn?.weightedDate, teamStartDate]);

  const selectedSubChallengeName = useMemo(() => {
    if (data?.campaign?.configJson?.campaignMode === 'MULTI' && data?.subChallengeId) {
      return subChallenges.find((sc: DynamicObjectInterface) => sc.subChallengeId === data?.subChallengeId)?.name ?? '';
    }
    return '';
  }, [data?.campaign?.configJson, data?.subChallengeId, subChallenges]);

  // Admin can set a per-sub-challenge assessment title; otherwise fall back to the default label.
  const assessmentTitle = useMemo(() => {
    if (data?.campaign?.configJson?.campaignMode === 'MULTI' && data?.subChallengeId) {
      const sc = subChallenges.find((i: DynamicObjectInterface) => i.subChallengeId === data?.subChallengeId);
      if (sc?.assessmentTitle) return sc.assessmentTitle as string;
    }
    return t('dashboard.my-progress.assessmentTitle');
  }, [data?.campaign?.configJson, data?.subChallengeId, subChallenges, t]);

  // useEffect(() => {
  //   (async () => {
  //     let apiList = [apiCall(CoachService.getCoachList)];
  //     let res = await Promise.all(apiList);

  //     let coachData = res?.[0]?.data;

  //     let masterList = {
  //       coachList: [
  //         ...(coachData?.data || []).map((d: DynamicObjectInterface) => ({
  //           ...d,
  //           id: d.coachID,
  //           title: `${d.coachID} - ${d.name}`,
  //         })),
  //       ],
  //     };
  //     setMasterList(masterList);
  //   })();
  // }, []);

  // useEffect(() => {
  //   if (data?.subscribe && data?.subscribe.length > 0) {
  //     setAlpValue(prev => ({ ...prev, input: data?.subscribe?.map(i => i.orderNumber)?.join(', ') ?? '' }));
  //   }
  // }, [data?.subscribe]);

  const deepClone = (obj: DynamicObjectInterface): DynamicObjectInterface => {
    if (obj === null || typeof obj !== 'object') return obj;

    if (obj instanceof Array) {
      const arrCopy = [];
      for (let i = 0; i < obj.length; i++) {
        arrCopy[i] = deepClone(obj[i]);
      }
      return arrCopy;
    }

    if (obj instanceof File) {
      return obj;
    }

    if (obj instanceof Object) {
      const objCopy: DynamicObjectInterface = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          objCopy[key] = deepClone(obj[key]);
        }
      }
      return objCopy;
    }
  };

  const handleOnChange = (value: DynamicObjectInterface, key: string, ...paths: string[]) => {
    setFormData(prev => {
      if (!prev) return null; // If prev is null, just return null

      // Create a deep copy of the previous state
      const updatedFormData = deepClone(prev);

      if (key === 'subscribe') {
        if (updatedFormData[key].length) {
          updatedFormData[key][0].orderNumber = value;
        } else {
          updatedFormData[key].push({
            orderNumber: value,
            orderPoint: 1,
          });
        }
      } else if (key === 'subscribeCheck' && updatedFormData.subscribeCheck === 'on') {
        updatedFormData.subscribeCheck = '';
      } else if (key === 'coachCheck' && updatedFormData.coachCheck === 'on') {
        updatedFormData.coachCheck = '';
      } else {
        const setDeep = (
          obj: DynamicObjectInterface,
          paths: string[],
          value: DynamicObjectInterface,
        ): DynamicObjectInterface => {
          const key = paths[0];
          if (paths.length === 1) {
            return {
              ...obj,
              [key]: value,
            };
          }

          return {
            ...obj,
            [key]: setDeep(obj[key] ?? {}, paths.slice(1), value),
          };
        };

        if (paths.length > 0) {
          updatedFormData[key] = setDeep(updatedFormData[key] || {}, paths, value);
        } else {
          updatedFormData[key] = value;
        }
      }

      return updatedFormData;
    });
  };

  const handleFile = async (e: customEventPropType, fieldCode: string) => {
    // Initialize or reset progress for the specific field
    setProgress(prev => ({ ...prev, [fieldCode]: [] }));

    const res = validateAttachment(e, t, 10); // Validate files
    if (res?.status) {
      const selectedFiles = e.files;
      if (selectedFiles) {
        // Map the selected files to a new file structure with URL
        const newFiles = Object.entries(selectedFiles).map(([_, file]) => ({
          file,
          url: URL.createObjectURL(file),
        }));

        // Simulate the upload progress for each file
        await new Promise<void>(resolve => {
          const interval = setInterval(() => {
            setProgress(prev => {
              const currentProgress = prev[fieldCode] ?? [];
              const updatedProgress = [...currentProgress];

              newFiles.forEach((file, idx) => {
                const diff = Math.random() * 70;
                updatedProgress[idx] = {
                  file: file,
                  percentage: Math.min((updatedProgress[idx]?.percentage ?? 0) + diff, 100),
                };
              });

              // Check if all files have reached 100% progress
              if (updatedProgress.every(e => e.percentage === 100)) {
                clearInterval(interval);
                resolve(); // Resolve when all progress is 100%
              }

              return {
                ...prev,
                [fieldCode]: updatedProgress,
              };
            });
          }, 500); // Update progress every 500ms
        });

        // Update the file state with the newly selected files for the specific field
        setFiles(prevFiles => {
          const updatedFiles = {
            ...prevFiles,
            [fieldCode]: [...(prevFiles[fieldCode] ?? []), ...newFiles],
          };
          return updatedFiles;
        });
      }
    } else {
      // If validation fails, show an error message
      adminToast({
        message: getTranslatedMessage(t, res?.message ?? ''),
        theme: Theme.ERROR,
      });
    }
  };

  const handleDelete = (index: number, fieldCode: string) => {
    setFiles(prevFiles => {
      const updatedFiles = (prevFiles[fieldCode] ?? []).filter((_, i) => i !== index);
      return {
        ...prevFiles,
        [fieldCode]: updatedFiles,
      };
    });

    setProgress(prevProgress => {
      const updatedProgress = (prevProgress[fieldCode] ?? []).filter((_, i) => i !== index);
      return {
        ...prevProgress,
        [fieldCode]: updatedProgress,
      };
    });
  };

  // const handleDownload = async (photoId: number) => {
  //   try {
  //     const response = await apiCall(AttachmentService.downloadSelected, { recId: photoId });

  //     if (response.status === 200 && !response.data?.data?.message) {
  //       const blob = response.data as Blob;
  //       const link = document.createElement('a');

  //       const extension = blob.type.split('/')[1] || 'bin';
  //       link.href = window.URL.createObjectURL(blob);
  //       link.setAttribute('download', `result.${extension}`); // e.g., result.jpeg

  //       document.body.appendChild(link);
  //       link.click();
  //       document.body.removeChild(link);
  //       URL.revokeObjectURL(link.href);
  //     } else {
  //       adminToast({
  //         message: response?.data?.data?.message,
  //         theme: Theme.ERROR,
  //       });
  //     }
  //   } catch (error) {
  //     LoggerService.log('Error downloading attachment', error);
  //   }
  // };

  // const handleOnAlpChange = (value: DynamicObjectInterface, key: 'input' | 'check') => {
  //   setAlpValue(prev => {
  //     return {
  //       ...prev,
  //       [key]: value,
  //     };
  //   });
  // };

  const rewardGiftModal = data?.activities?.map((activity) => {
    const reward = activity?.reward;
    return reward?.map(r => (
      <div className={styles['reward-container']}>
        <div>
          {r.imageURL ? (
            <img className={styles['reward-container-img']} src={r.imageURL} alt={'reward'} />
          ) : (
            <div className={styles['reward-container-img']}>
              <FAIcon
                iconStyle={IconStyle.SOLID}
                iconName={'gift'}
                theme={Theme.BLACK}
                className={styles['reward-container-icon']}
              />
            </div>
          )}
        </div>
        <div className={styles['reward-container-wrapper']}>
          <div>
            <div className={styles['reward-container-title']}>{t('dashboard.my-progress.yourChosenReward')}</div>
            <div className={styles['reward-container-subtitle']}>{r.rewardName}</div>
          </div>
          <div className={styles['reward-container-status-wrapper']}>
            <div className={styles['reward-container-status-title']}>{t('dashboard.my-progress.status')} </div>
            <div className={styles['reward-container-status-tag']}>
              {r.rewardStatus && getRewardStatus(t, r.rewardStatus, '')}{' '}
              {formatDateDMYYYY(new Date(convertGMT8date(r.modifiedDate)))}
            </div>
          </div>
        </div>
      </div>
    ));
  });

  // const onSave = async () => {
  //   let payload = {
  //     campaignId: campaignData[index]?.campaignId,
  //     teamId: data?.teamId,
  //     allocation: {
  //       alp: {
  //         invoices: [
  //           {
  //             recId: formData?.subscribe[0]?.recId || null,
  //             orderPoint: 1,
  //             orderNumber: alpValue.input,
  //           },
  //         ],
  //       },
  //     },
  //   };

  //   const updatedPayload = new FormData();
  //   updatedPayload.append('payload', JSON.stringify(payload));
  //   const result = await apiCall(
  //     ParticipantService.updateParticipantDetails,
  //     updatedPayload,
  //     'pointAllocation',
  //     decodedData.userId,
  //   );

  //   if (result.status === 200) {
  //     adminToast({
  //       message: t('dashboard.my-progress.updateSuccess'),
  //       theme: Theme.SUCCESS,
  //     });
  //     fetchData();
  //     // setShowConfirmation(false);
  //   } else {
  //     adminToast({
  //       message: t('dashboard.my-progress.updateFail'),
  //       theme: Theme.ERROR,
  //     });
  //   }
  // };

  const fetchMeasurementImage = async () => {
    try {
      const payload = {
        campaignId: campaignData[index]?.campaignId,
        teamId: data?.teamId,
        // Use the viewed member in read-only mode, else the logged-in user.
        loginId: targetUserId,
      };
      const response = await AttachmentService.downloadBeforeAfter(payload);

      // Validate response structure
      const imageData = response?.data?.data?.image || [];

      // Update state variables based on the downloaded image data
      const updatedImages: Record<string, AttachmentImageBA> = {
        before_front: { photoId: 0, url: '', file: null },
        after_front: { photoId: 0, url: '', file: null },
        before_side: { photoId: 0, url: '', file: null },
        after_side: { photoId: 0, url: '', file: null },
      };

      let weightInFiles: { [fieldCode: string]: { file?: File; url: string; type?: string; photoId?: number }[] } = {};
      let weightOutFiles: { [fieldCode: string]: { file?: File; url: string; type?: string; photoId?: number }[] } = {};
      const checkInFileMap: { [day: number]: { photoId: number; fileName: string; type?: string }[] } = {};
      // Derive a readable file name from the (URL-encoded) S3 key.
      const fileNameFromUrl = (u: string): string => {
        try {
          const path = decodeURIComponent(u.split('?')[0]);
          return path.substring(path.lastIndexOf('/') + 1) || 'attachment';
        } catch {
          return 'attachment';
        }
      };
      imageData.forEach((image: { photoId: number; photoType: string; url: string; type: string }) => {
        const { photoId, photoType, url, type } = image;
        if (Object.prototype.hasOwnProperty.call(updatedImages, photoType)) {
          updatedImages[photoType].url = url;
          updatedImages[photoType].photoId = photoId; // Assigning index from API response
        }
        const key = photoType.includes('before_result')
          ? photoType.split('before_result_')[1]
          : photoType.split('after_result_')[1];

        if (photoType.includes('before_result')) {
          if (!weightInFiles[key]) weightInFiles[key] = [];
          weightInFiles[key].push({ url, type, photoId });
        } else if (photoType.includes('after_result')) {
          if (!weightOutFiles[key]) weightOutFiles[key] = [];
          weightOutFiles[key].push({ url, type, photoId });
        } else if (photoType.startsWith('checkin_day')) {
          // photoType format: checkin_day{N}_{fieldCode}_{idx}
          const day = Number(photoType.replace('checkin_day', '').split('_')[0]);
          if (!isNaN(day)) {
            if (!checkInFileMap[day]) checkInFileMap[day] = [];
            checkInFileMap[day].push({ photoId, fileName: fileNameFromUrl(url), type });
          }
        }
      });

      setData(i => {
        if (i && i.weightIn && i.weightOut) {
          i.weightIn.file = weightInFiles;
          i.weightOut.file = weightOutFiles;
          i.images = updatedImages;
          return { ...i };
        }
        return i;
      });
      setImages(updatedImages);
      setCheckInFiles(checkInFileMap);
    } catch (error) {
      LoggerService.log('Error downloading image', error);
    }
  };

  useEffect(() => {
    if (data?.teamId) {
      fetchMeasurementImage();
    }
  }, [data?.teamId]);

  // const searchCoach = (input: string) => {
  //   if (!input) {
  //     setFilteredCoachList(masterList?.coachList);
  //   } else {
  //     const filteredUsers = masterList?.coachList.filter((coach: DynamicObjectInterface) => {
  //       return coach.title.toLowerCase().includes(input.toLowerCase());
  //     });
  //     setFilteredCoachList(filteredUsers);
  //   }
  // };

  // const confirmationPrompt = (
  //   <>
  //     {showConfirmation && (
  //       <Modal
  //         primaryButtonLabel={''}
  //         headline={'Add ALP Order'}
  //         children={
  //           <>
  //             <div
  //               style={{
  //                 fontSize: '16px',
  //                 fontFamily: 'GT-Walsheim-Pro-Bold, sans-serif',
  //                 lineHeight: '24px',
  //                 margin: '10px 0',
  //               }}
  //             >
  //               Enter your ALP Subscription Order Number
  //             </div>
  //             <Input
  //               type={InputTypes.TEXT}
  //               name={'orderNumber'}
  //               label={t('admin.participant.alpInvoiceNumber')}
  //               value={alpValue.input}
  //               mlpVersion={MLPVersion.ONE}
  //               onChange={e => handleOnAlpChange(e.target.value || null, 'input')}
  //             />
  //             <div style={{ display: 'flex', margin: '32px 0' }}>
  //               <Checkbox
  //                 key={'checkbox'}
  //                 className={styles['checkbox']}
  //                 checked={alpValue.check}
  //                 theme={Theme.BLACK}
  //                 text={''}
  //                 onChange={e => handleOnAlpChange(e.target.value === 'on', 'check')}
  //               />
  //               <>
  //                 {/* {t('admin.teamCreate.termsLine1')} */}
  //                 <br />
  //                 {/* {t('admin.teamCreate.termsLine2')} */}
  //               </>
  //             </div>
  //           </>
  //         }
  //         closeIcon={false}
  //         containerWidth={'max-content'}
  //         textAlign={TextAlign.LEFT}
  //         primaryButton={
  //           <Button
  //             title="Confirm"
  //             type={ButtonTypes.BUTTON}
  //             onClick={() => {
  //               onSave();
  //             }}
  //             size={ButtonSize.SMALL}
  //             buttonStyle={ButtonStyles.SOLID}
  //             theme={Theme.GREEN}
  //             data-testid="test-add-button-popup"
  //             disabled={!alpValue.check}
  //           />
  //         }
  //         secondaryButton={
  //           <Button
  //             title="Cancel"
  //             type={ButtonTypes.BUTTON}
  //             onClick={() => {
  //               setAlpValue({ input: data?.subscribe?.map(i => i.orderNumber)?.join(', ') ?? '', check: false });
  //               setFormData(data);
  //               // setShowConfirmation(false);
  //             }}
  //             size={ButtonSize.SMALL}
  //             buttonStyle={ButtonStyles.OUTLINE}
  //             theme={Theme.GREEN}
  //             data-testid="test-cancel-button-popup-delete-modal"
  //           />
  //         }
  //       />
  //     )}
  //   </>
  // );

  // const coachSelectionPrompt = (
  //   <>
  //     {showCoachList && (
  //       <Modal
  //         primaryButtonLabel={''}
  //         headline={'Add Coach'}
  //         closeIcon={false}
  //         containerWidth="550px"
  //         textAlign={TextAlign.LEFT}
  //         primaryButton={
  //           <Button
  //             title="Confirm"
  //             type={ButtonTypes.BUTTON}
  //             onClick={() => {
  //               onSaveCoach();
  //             }}
  //             size={ButtonSize.SMALL}
  //             buttonStyle={ButtonStyles.SOLID}
  //             data-testid="test-add-button-popup"
  //             disabled={!(formData?.teamCoachId && formData?.coachCheck === 'on')}
  //           />
  //         }
  //         secondaryButton={
  //           <Button
  //             title="Cancel"
  //             type={ButtonTypes.BUTTON}
  //             onClick={() => {
  //               setFormData(data);
  //               setShowCoachList(false);
  //             }}
  //             size={ButtonSize.SMALL}
  //             buttonStyle={ButtonStyles.OUTLINE}
  //             theme={Theme.BLACK}
  //             data-testid="test-cancel-button-popup-delete-modal"
  //           />
  //         }
  //       >
  //         <>
  //           <div
  //             style={{
  //               fontSize: '16px',
  //               fontFamily: 'GT-Walsheim-Pro-Bold, sans-serif',
  //               lineHeight: '24px',
  //               margin: '10px 0',
  //             }}
  //           ></div>
  //           <div className={styles['search-coach-container']}>
  //             <SearchComponent
  //               status={InputValidations.DEFAULT}
  //               label={t('admin.teams.coachId')}
  //               placeholder={t('admin.teams.searchCoach')}
  //               initialSearchText={
  //                 masterList?.coachList.find((c: DynamicObjectInterface) => c.id === data?.teamCoachId)?.title
  //               }
  //               labelChoice={'title'}
  //               dropdownOptions={filteredCoachList}
  //               onSearch={searchCoach}
  //               onClear={() => {
  //                 searchCoach('');
  //                 handleOnChange('', 'teamCoachId');
  //               }}
  //               onSelect={e => {
  //                 handleOnChange(e.id, 'teamCoachId');
  //               }}
  //             />
  //           </div>
  //           <div style={{ display: 'flex', margin: '32px 0' }}>
  //             <Checkbox
  //               key={'checkbox'}
  //               className={styles['checkbox']}
  //               checked={formData?.coachCheck === 'on'}
  //               theme={Theme.BLACK}
  //               text={''}
  //               onChange={e => handleOnChange(e.target.value, 'coachCheck')}
  //             />
  //             <>
  //               {t('admin.teamCreate.termsLine1')}
  //               <br />
  //               {/* {t('admin.teamCreate.termsLine2')} */}
  //             </>
  //           </div>
  //         </>
  //       </Modal>
  //     )}
  //   </>
  // );

  const RoleList = (): string => {
    let roles: string[] = [];

    if (campaignData[index]?.enrollment?.length > 0) {
      const additionalRoles = campaignData[index].enrollment
        .map(enrollment => {
          if (enrollment.enrollmentType === 'PARTICIPANT') return getEnrollmentType(t, EnrollmentType.PARTICIPANT);
          if (enrollment.enrollmentType === 'COACH') return getEnrollmentType(t, EnrollmentType.COACH);
          // if (enrollment.enrollmentType === 'LEADER') return 'Leader';
          return null;
        })
        .filter(role => role !== null) as string[];

      if (additionalRoles.length > 0) {
        roles = additionalRoles;
      }
    }

    return roles.join(', ');
  };

  const getUserAnswer = async () => {
    try {
      const payload = {
        userId: decodedData.userId,
        campaignId: campaignData[index]?.campaignId,
      };
      const response = await healthGoalService.userAnswer(payload);
      if (response.status === 200) {
        let responseData = response.data.data;

        // Parse options if it is a string
        if (responseData && typeof responseData.options === 'string') {
          try {
            // responseData.options = JSON.parse(responseData.options);
            responseData.options = JSON.parse(responseData.options);
          } catch (error) {
            LoggerService.log('Error parsing options', error);
            responseData.options = null; // or handle it appropriately
          }
        }

        setUserAnswerData(responseData);
      }
    } catch (error) {
      LoggerService.log('Error retrieving question', error);
    }
  };

  const [campaignIconURL, setCampaignIconURL] = useState('');
  const cacheKey = 'CampaignIconURL';
  const getCampaignIcon = async () => {
    try {
      const data = await getContent('challenge_details', cacheKey, true);
      if (data && data.length > 0) {
        const challenge = data.find((item: ContentItem) => item.campaign_id === campaignData[index].campaignId);
        setCampaignIconURL(challenge.challenge_icon.url || '');
      }
    } catch (error) {
      LoggerService.log('Failed to retrieve content data. Using default content.');
    }
  };

  useEffect(() => {
    getCampaignIcon();
    getUserAnswer();
  }, [location]);

  const formatCampaignDescription = (description: string) => {
    return description.split('\n').map((line, index) => (
      <div className={styles['formatted-description']} key={index}>
        {line}
      </div>
    ));
  };

  const userDetail = (
    <Card containerHeight="100%">
      {/* campaign details section */}
      <div className={styles['campaign-header']}>
        <div className={styles['campaign-header-top']}>
          <img
            src={campaignIconURL !== '' ? campaignIconURL : CampaignLogo}
            alt="campaign"
            className={styles['campaign-header-logo']}
          />
          <div className={styles['campaign-header-title']}>{campaignData[index]?.campaignName ?? ''}</div>
        </div>
        <div className={styles['campaign-header-info']}>
          <div className={styles['campaign-header-info-item']}>
            <div className={styles['campaign-header-info-label']}>
              {t('dashboard.my-progress.campaignPeriodLabel')}
            </div>
            <div className={styles['campaign-header-info-value']}>{`${dateTimeToCustomFormat(
              campaignDetail?.campaignStart?.toString() ?? data?.campaign?.campaignStart?.toString() ?? '',
              'DD/MM/YYYY',
            )} - ${dateTimeToCustomFormat(
              campaignDetail?.campaignEnd?.toString() ?? data?.campaign?.campaignEnd?.toString() ?? '',
              'DD/MM/YYYY',
            )}`}</div>
          </div>
          <div className={styles['campaign-header-divider']} />
          <div className={styles['campaign-header-info-item']}>
            <div className={styles['campaign-header-info-label']}>{t('dashboard.my-progress.campaignInfoLabel')}</div>
            <div className={styles['campaign-header-info-value']}>
              {campaignDetail?.campaignDescription
                ? formatCampaignDescription(campaignDetail.campaignDescription)
                : data?.campaign?.campaignDescription
                  ? formatCampaignDescription(data.campaign.campaignDescription)
                  : ''}
            </div>
          </div>
        </div>
        {/* Member details — only when viewing another member (read-only). */}
        {readOnly && (
          <div className={styles['campaign-header-info-member']}>
            {/* Viewed member's profile photo (initials fallback when none). */}
            <ProfileImage
              placeholder={data?.name ?? ''}
              src={memberProfileImageUrl}
              classes={styles['campaign-header-member-avatar']}
            />
            <div className={styles['campaign-header-info-member-grid']}>
              <div className={styles['campaign-header-info-item']}>
                <div className={styles['campaign-header-info-label']}>{t('dashboard.my-progress.submissionName')}</div>
                <div className={styles['campaign-header-info-value']}>{data?.name ?? '-'}</div>
              </div>
              <div className={styles['campaign-header-info-item']}>
                <div className={styles['campaign-header-info-label']}>{t('dashboard.my-progress.memberContactNo')}</div>
                <div className={styles['campaign-header-info-value']}>{data?.phone ?? '-'}</div>
              </div>
              <div className={styles['campaign-header-info-item']}>
                <div className={styles['campaign-header-info-label']}>{t('dashboard.my-progress.submissionABOId')}</div>
                <div className={styles['campaign-header-info-value']}>{data?.loginId ?? '-'}</div>
              </div>
              <div className={styles['campaign-header-info-item']}>
                <div className={styles['campaign-header-info-label']}>{t('dashboard.my-progress.memberEmail')}</div>
                <div className={styles['campaign-header-info-value']}>{data?.email ?? '-'}</div>
              </div>
            </div>
          </div>
        )}
      </div>
      {REACT_APP_COUNTRY !== 'MY' && (
        <div className={`${styles['details']} ${isCoach && styles['details-flex']}`}>
          <div className={styles['details-info']}>
            <div className={styles['details-info-item']}>
              <div className={styles['label']}>{t('dashboard.my-progress.myRoleLabel')}</div>
              <div className={styles['label-content']}>{RoleList()}</div>
            </div>
          </div>
        </div>
      )}
      {REACT_APP_COUNTRY !== 'MY' &&
        userAnswerData !== null &&
        userAnswerData?.options &&
        data?.campaign?.configJson?.campaignIsHealthGoalSectionShown === 1 && (
          <>
            <div className={styles['progress-monitoring-horizontal-line']} />
            {/* health goal section  */}
            <div>
              <div className={styles['progress-monitoring-title-container']}>
                <div className={styles['progress-monitoring-title-container-title']}>
                  {t('dashboard.my-progress.healthGoalTitle')}
                </div>
              </div>

              <div className={styles['health-goal-section']}>
                {/* <div className={styles['health-goal-content']}> */}
                {Array.isArray(userAnswerData?.options) &&
                  userAnswerData.options.map(option => (
                    <div key={option.key} className={styles['health-goal-item']}>
                      {option.title}
                    </div>
                  ))}
                {/* </div> */}
              </div>
            </div>
          </>
        )}
    </Card>
  );

  const handleFileImage = (e: customEventPropType, view: string, isBefore: boolean) => {
    const file = e.files[0];
    // Check if the user has selected a file
    const imageUrlSetter = isBefore ? 'before' : 'after';
    const imageStateSetter = view === 'front' ? 'front' : 'side';
    if (file) {
      // Check if the original filename is "image.jpg"
      const originalFileName = file.name;
      const isOriginalImageName = originalFileName === 'image.jpg';

      // Get current date and time to create a unique filename
      const timestamp = currentDate.getTime(); // Get timestamp in milliseconds
      const extension = file.name.split('.').pop(); // Get the file extension

      let newFileName = originalFileName; // Default to original filename
      if (isOriginalImageName) {
        newFileName = `${timestamp}.${extension}`; // Create new filename with timestamp
      }

      setImages((prevImages: Record<string, AttachmentImageBA>) => ({
        ...prevImages,
        [`${imageUrlSetter}_${imageStateSetter}`]: {
          photoId: prevImages[`${imageUrlSetter}_${imageStateSetter}`].photoId,
          url: URL.createObjectURL(file),
          file: new File([file], newFileName), // Create a new File object with the renamed file if needed
        },
      }));
    } else {
      setImages((prevImages: Record<string, AttachmentImageBA>) => ({
        ...prevImages,
        [`${imageUrlSetter}_${imageStateSetter}`]: {
          photoId: prevImages[`${imageUrlSetter}_${imageStateSetter}`].photoId,
          url: '',
          file: null,
        },
      }));
    }
  };

  const [openProgressModal, setOpenProgressModal] = useState(false);
  const [deleteProgressModal, setDeleteProgressModal] = useState(false);
  const [disabledDates, setDisabledDates] = useState<Date[]>([]);
  // const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [singleProgressData, setSingleProgressData] = useState<DynamicObjectInterface>({
    weighingDate: new Date(),
    checked: false,
  });
  const [progressData, setProgressData] = useState<ProgressType[]>([]);
  const [progressRowRecId, setProgressRowRecId] = useState<{
    rowObject?: DynamicObjectInterface;
    index?: DynamicObjectInterface;
  }>({});

  const handleProgressUpdate = (name: string, value: DynamicObjectInterface) => {
    setSingleProgressData((prevProgressData: DynamicObjectInterface) => ({
      ...prevProgressData!,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (!openProgressModal) {
      setFiles({});
      setProgress({});
    }
  }, [openProgressModal]);

  const fetchProgressMonitoringList = async () => {
    const info = {
      loginId: decodedData.loginId,
      userId: decodedData.userId,
      campaignId: campaignData[index]?.campaignId,
    };

    try {
      const result = await apiCall(intermediateMeasurementService.getIntermediateMeasurementList, info);
      setProgressData(result?.data?.data);
    } catch (error) {
      LoggerService.log('Failed to retrieve campaign list', error);
    }
  };

  useEffect(() => {
    fetchProgressMonitoringList();
  }, []);

  const handleSaveProgressDetails = async () => {

    const payload = new FormData();

    const updatedFormData = {
      loginId: decodedData.loginId,
      userId: decodedData.userId,
      partyId: decodedData.partyId,
      campaignId: campaignData[index]?.campaignId,
      weightMachine: data?.weightIn?.machine,
      otherIndicators: activeSubKpiCode
        ?.filter((i: DynamicObjectInterface) => i.displayInProgressMonitoring === 'on')
        .reduce((acc: Record<string, string>, i: DynamicObjectInterface) => {
          acc[i.subKpiCode] = encryptString(singleProgressData[i.subKpiCode]?.toString() ?? '');
          return acc;
        }, {}),
      remarks: '',
      teamId: data?.teamId,
      weighingDate: dateTimeToCustomFormat(singleProgressData.weighingDate.toString(), 'YYYY-MM-DD'),
      acknowledgedDate: dateTimeToCustomFormat(new Date().toString(), 'YYYY-MM-DD'),
      acknowledgeBy: decodedData.userId,
    };

    payload.append('payload', JSON.stringify(updatedFormData));
    
    Object.entries(files).forEach(([fieldCode, fileList]) => {
      fileList.forEach((fileObj, idx) => {
        payload.append(
          `photoId.0,${fieldCode}_${idx}`,
          fileObj.file
        );
      });
    });

    const result = await apiCall(intermediateMeasurementService.addIntermediateMeasurement, payload);

    if (result.status === 200) {
      adminToast({
        message: t('dashboard.my-progress.progressMonitoringUpdateSuccess'),
        theme: Theme.SUCCESS,
      });
      fetchProgressMonitoringList();
      setOpenProgressModal(false);
      setSingleProgressData({
        weighingDate: new Date(),
        checked: false,
      });
    } else {
      adminToast({
        message: t('dashboard.my-progress.updateFail'),
        theme: Theme.ERROR,
      });
    }
  };

  const subKpiListDisplayInProgressMonitoring =
    activeSubKpiCode?.filter((i: DynamicObjectInterface) => i.displayInProgressMonitoring === 'on') || [];

  const progressMonitorConfig = [
    {
      key: 'weighingDate',
      text: t('dashboard.my-progress.input.date'),
      sortable: true,
      width: isDesktopView ? 20 : 25,
      render: (object: DynamicObjectInterface) => (
        <span>{dateTimeToCustomFormat(object.weighingDate, 'DD/MM/YYYY')}</span>
      ),
    },
    ...(subKpiListDisplayInProgressMonitoring || []).map((kpi: DynamicObjectInterface) => ({
      key: kpi.subKpiCode,
      text: `${kpi.indicatorName} (${kpi.indicatorMeasurementUnit})`,
      sortable: false,
      // width: '',
      render: (object: DynamicObjectInterface) => {
        const otherIndicators = JSON.parse(object.otherIndicators);
        return (
          <span>
            {otherIndicators[kpi.subKpiCode]
              ? getRoundedNumber(parseFloat(decryptString(otherIndicators[kpi.subKpiCode])))
              : ''}
          </span>
        );
      },
    })),
  ];

  const handleDeleteProgress = async () => {
    const result = await apiCall(
      intermediateMeasurementService.deleteIntermediateMeasurement,
      progressRowRecId.rowObject.recId,
    );

    if (result.status === 200) {
      adminToast({
        message: t('dashboard.my-progress.progressMonitoringDeleteSuccess'),
        theme: Theme.SUCCESS,
      });
      fetchProgressMonitoringList();
      setDeleteProgressModal(false);
    } else {
      adminToast({
        message: t('dashboard.my-progress.updateFail'),
        theme: Theme.ERROR,
      });
    }
  };


  const downloadIntermediateResult = async (row :any) => {

    try {
      const payload = {
        loginId: decodedData?.userId,
        campaignId: campaignData[index]?.campaignId,
        teamId: data?.teamId,
        assessmentType: 'intermediate',
        refId: row.recId,
      };

      const recordDate = row.weighingDate.split(' ')[0].replaceAll('-', '');

      const response = await apiCall(AttachmentService.downloadOther, payload);

      //console.log('response', response);
      if (response.status === 200 && !response.data?.data?.message) {
        const data = response.data;
        const blob = new Blob([data], { type: 'application/zip' });

        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', recordDate + '_'  + decodedData?.userId +  '_intermediate_result.zip');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Release the object URL resources
        URL.revokeObjectURL(link.href);
      } else {
        adminToast({
          message: response?.data?.data?.message,
          theme: Theme.ERROR,
        });
      }
    } catch (error) {
      LoggerService.log('Error downloading attachment', error);
    }
  };

  const deleteProgressMonitorRec = (rowObject: DynamicObjectInterface, index: DynamicObjectInterface) => {
    setProgressRowRecId({ rowObject, index });
    setDeleteProgressModal(true);
  };

  const generateDisabledDates = (referenceDate: Date): Date[] => {
    const disabledDates: Date[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Ensure 'today' is at midnight

    // The day before the reference date
    let inputDate = new Date(referenceDate);
    inputDate.setHours(0, 0, 0, 0);
    inputDate.setDate(inputDate.getDate() - 1);

    // Set the earliest date to consider
    const oneYearBefore = new Date(inputDate);
    oneYearBefore.setFullYear(oneYearBefore.getFullYear() - 1);

    // Collect all dates before the reference date
    while (inputDate >= oneYearBefore) {
      disabledDates.push(new Date(inputDate));
      inputDate.setDate(inputDate.getDate() - 1);
    }

    // Collect all dates after today
    let futureDate = new Date(today);
    futureDate.setDate(futureDate.getDate() + 1);

    // Loop until we reach a reasonable future limit
    const futureLimit = new Date(today);
    futureLimit.setFullYear(today.getFullYear() + 1);

    while (futureDate <= futureLimit) {
      disabledDates.push(new Date(futureDate));
      futureDate.setDate(futureDate.getDate() + 1);
    }

    if (progressData) {
      progressData.forEach(data => {
        const weightedDate = new Date(data.weighingDate);
        weightedDate.setHours(0, 0, 0, 0);
        disabledDates.push(weightedDate);
      });
    }

    // Convert to timestamp for comparison and uniqueness
    const uniqueDisabledDates = Array.from(new Set(disabledDates.map(date => date.getTime())));

    return uniqueDisabledDates.map(time => new Date(time));
  };

  const isIndicatorValueValid = (value?: string, min?: string, max?: string): boolean => {
    if (!value) return true;
    const num = parseFloat(value);
    return (
      (!min || min === undefined || num >= parseFloat(min)) && (!max || max === undefined || num <= parseFloat(max))
    );
  };

  const isValidateInputs = (): boolean => {
    const indicators = activeSubKpiCode;

    return (
      indicators?.every((i: DynamicObjectInterface) => {
        const value = singleProgressData[i.subKpiCode];

        const isValid = (val?: string) => {
          if (!val) return true;
          const num = parseFloat(val);
          return (
            (!i.indicatorMin || i.indicatorMin === undefined || num >= parseFloat(i.indicatorMin)) &&
            (!i.indicatorMin || i.indicatorMax === undefined || num <= parseFloat(i.indicatorMax))
          );
        };

        return isValid(value);
      }) ?? true
    ); // fallback to true if indicators undefined
  };

  useEffect(() => {
    if (formData?.weightIn?.weightedDate) {
      const referenceDate = new Date(formData.weightIn.weightedDate);
      const disabledDates = generateDisabledDates(referenceDate);
      setDisabledDates(disabledDates);
    }
  }, [formData, progressData]);

  const addProgress = (
    <>
      {openProgressModal &&
        (isDesktopView ? (
          <Modal
            headline={t('dashboard.my-progress.progressMonitoringAddNew')}
            textAlign={TextAlign.LEFT}
            containerWidth="750px"
            // containerHeight="800px"
            buttonHorizontalAlignment={TextAlign.RIGHT}
            closeIcon={false}
            primaryButton={
              <Button
                title={t('dashboard.my-progress.button.confirm')}
                type={ButtonTypes.BUTTON}
                theme={Theme.GREEN}
                onClick={() => {
                  handleSaveProgressDetails();
                }}
                size={ButtonSize.MEDIUM}
                disabled={
                  !subKpiListDisplayInProgressMonitoring?.every((i: DynamicObjectInterface) => {
                    return !!singleProgressData[i.subKpiCode];
                  }) ||
                  !singleProgressData.checked ||
                  !isValidateInputs()
                }
                buttonStyle={ButtonStyles.SOLID}
              />
            }
            secondaryButton={
              <Button
                title={t('dashboard.my-progress.button.cancel')}
                type={ButtonTypes.BUTTON}
                onClick={() => {
                  setOpenProgressModal(false);
                }}
                size={ButtonSize.MEDIUM}
                buttonStyle={ButtonStyles.OUTLINE}
                theme={Theme.GREEN}
              />
            }
          >
            <div className={styles['progress-monitoring-modal']}>
              {/* top  */}
              <div className={styles['progress-monitoring-modal-top']}>
                {t('dashboard.my-progress.progressMonitoringModalDesc')}
              </div>
              {/* center  */}
              <div className={styles['progress-monitoring-modal-center']}>
                <DatePicker
                  inputContainerHeight="40px"
                  showDateContainer
                  // calendarHeading={t('admin.campaignConfig.startDate')}
                  value={singleProgressData.weighingDate}
                  onChange={date => {
                    handleProgressUpdate('weighingDate', date);
                  }}
                  actionButtonStyle={{ padding: '5px 10px 0' }}
                  calendarContainerStyling={isDesktopView && { padding: '10px', top: '-300px', left: '210px' }}
                  menuContainerStyle={{ position: 'fixed', zIndex: 2 }}
                  required
                  containerWidth="450px"
                  inputLabel={t('dashboard.my-progress.input.date')}
                  disabledDates={disabledDates}
                />
                {subKpiListDisplayInProgressMonitoring?.map((i: DynamicObjectInterface) => {
                  const isValid = isIndicatorValueValid(
                    singleProgressData[i.subKpiCode],
                    i.indicatorMin,
                    i.indicatorMax,
                  );
                  return (
                    <Input
                      type={InputTypes.NUMBER}
                      label={`${i.indicatorName} (${i.indicatorMeasurementUnit})`}
                      name={`${i.indicatorName} (${i.indicatorMeasurementUnit})`}
                      placeholder={i.indicatorPlaceholder}
                      mlpVersion={MLPVersion.ONE}
                      value={singleProgressData[i.subKpiCode] || ''}
                      required
                      status={
                        (singleProgressData.checked && !singleProgressData[i.subKpiCode]) || !isValid
                          ? InputValidations.INVALID
                          : InputValidations.DEFAULT
                      }
                      message={
                        singleProgressData.checked && !singleProgressData[i.subKpiCode]
                          ? 'This field is mandatory.'
                          : !isValid
                            ? t('dashboard.my-progress.assessmentInputValidation')
                            : ''
                      }
                      onChange={e => handleProgressUpdate(i.subKpiCode, e.target.value)}
                    />
                  );
                })}
                {/* bottom  */}
              </div>
                <div className={styles['video-editor-wrapper']}>
                    <VideoEditor
                      onFileUpload={(e) => handleFile(e, 'intermediate')}
                      accept="image/*, application/pdf"
                      fileTypeLabel={t('dashboard.my-progress.allowedFileType')}
                      multiple={true}
                      files={files['intermediate'] ?? []}
                      onDelete={(index) => handleDelete(index, 'intermediate')}
                      //handleDownload={(id) => handleDownload(id)}
                      disabled={false}
                      progress={progress['intermediate'] ?? []}
                      dragCTALabel={t('dashboard.my-progress.dragAndDrop')}
                      connectorLabel={t('dashboard.my-progress.or')}
                      uploadCTALabel={t('dashboard.my-progress.selectFileToUpload')}
                      addAnotherFile={t('dashboard.my-progress.addAnotherFile')}
                    />
                </div>
                <div/>
              <div>
                <Checkbox
                  text={`${t('dashboard.my-progress.checkboxDesc')}`}
                  checked={singleProgressData.checked}
                  onChange={e => {
                    // setCheckboxChecked(e.target.checked);
                    handleProgressUpdate('checked', e.target.checked);
                  }}
                />
              </div>
            </div>
          </Modal>
        ) : (
          <Drawer
            onClose={() => setOpenProgressModal(false)}
            header={`${t('dashboard.my-progress.progressMonitoringAddNew')}`}
            className={styles['mobile-drawer-log']}
            closeOnBackdropClick={true}
            // closeIcon={true}
            bottomGap={0}
            footer={
              <div className={styles['drawer-buttons']}>
                <div className={styles['button-cancel']}>
                  <Button
                    title={t('dashboard.my-progress.button.cancel')}
                    type={ButtonTypes.BUTTON}
                    onClick={() => {
                      setOpenProgressModal(false);
                    }}
                    size={ButtonSize.MEDIUM}
                    buttonStyle={ButtonStyles.OUTLINE}
                    theme={Theme.GREEN}
                  />
                </div>
                <Button
                  title={t('dashboard.my-progress.button.confirm')}
                  type={ButtonTypes.BUTTON}
                  onClick={() => {
                    handleSaveProgressDetails();
                  }}
                  size={ButtonSize.MEDIUM}
                  disabled={
                    !subKpiListDisplayInProgressMonitoring?.every((i: DynamicObjectInterface) => {
                      return !!singleProgressData[i.subKpiCode];
                    }) ||
                    !singleProgressData.checked ||
                    !isValidateInputs()
                  }
                  buttonStyle={ButtonStyles.SOLID}
                  theme={Theme.GREEN}
                />
              </div>
            }
          >
            <div className={styles['progress-monitoring-modal']}>
              {/* top  */}
              <div className={styles['progress-monitoring-modal-top']}>
                {t('dashboard.my-progress.progressMonitoringModalDesc')}
              </div>
              {/* center  */}
              <div className={styles['progress-monitoring-modal-center']}>
                <div className={styles['progress-monitoring-modal-input']}>
                  <DatePicker
                    inputContainerHeight="40px"
                    showDateContainer
                    // calendarHeading={t('admin.campaignConfig.startDate')}
                    value={singleProgressData.weighingDate}
                    onChange={date => {
                      handleProgressUpdate('weighingDate', date);
                    }}
                    actionButtonStyle={{ padding: '5px 10px 0' }}
                    calendarContainerStyling={isDesktopView && { padding: '10px', top: '-300px', left: '210px' }}
                    menuContainerStyle={{ position: 'fixed', zIndex: 2 }}
                    required
                    containerWidth="450px"
                    inputLabel={t('dashboard.my-progress.input.date')}
                    disabledDates={disabledDates}
                  />
                </div>

                {subKpiListDisplayInProgressMonitoring?.map((i: DynamicObjectInterface) => {
                  const isValid = isIndicatorValueValid(
                    singleProgressData[i.subKpiCode],
                    i.indicatorMin,
                    i.indicatorMax,
                  );
                  return (
                    <Input
                      type={InputTypes.NUMBER}
                      label={`${i.indicatorName} (${i.indicatorMeasurementUnit})`}
                      name={`${i.indicatorName} (${i.indicatorMeasurementUnit})`}
                      placeholder={i.indicatorPlaceholder}
                      mlpVersion={MLPVersion.ONE}
                      value={singleProgressData[i.subKpiCode] || ''}
                      required
                      status={
                        (singleProgressData.checked && !singleProgressData[i.subKpiCode]) || !isValid
                          ? InputValidations.INVALID
                          : InputValidations.DEFAULT
                      }
                      message={
                        singleProgressData.checked && !singleProgressData[i.subKpiCode]
                          ? 'This field is mandatory.'
                          : !isValid
                            ? t('dashboard.my-progress.assessmentInputValidation')
                            : ''
                      }
                      onChange={e => handleProgressUpdate(i.subKpiCode, e.target.value)}
                    />
                  );
                })}
              </div>
              {/* bottom  */}
              <div>
                <Checkbox
                  text={`${t('dashboard.my-progress.checkboxDesc')}`}
                  checked={singleProgressData.checked}
                  onChange={e => {
                    // setCheckboxChecked(e.target.checked);
                    handleProgressUpdate('checked', e.target.checked);
                  }}
                />
              </div>
            </div>
          </Drawer>
        ))}
    </>
  );

  const deleteProgress = (
    <>
      {deleteProgressModal && (
        <Modal
          headline={''}
          textAlign={TextAlign.CENTER}
          containerWidth="550px"
          buttonHorizontalAlignment={TextAlign.CENTER}
          closeIcon={false}
          primaryButton={
            <Button
              title={t('dashboard.my-progress.button.delete')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                handleDeleteProgress();
                setDeleteProgressModal(false);
              }}
              buttonWidth={ButtonWidth.FIT_CONTENT}
              size={ButtonSize.MEDIUM}
              buttonStyle={ButtonStyles.SOLID}
              theme={Theme.GREEN}
            />
          }
          secondaryButton={
            <Button
              title={t('dashboard.my-progress.button.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setDeleteProgressModal(false);
              }}
              buttonWidth={ButtonWidth.FIT_CONTENT}
              size={ButtonSize.MEDIUM}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.GREEN}
            />
          }
        >
          <div className={styles['progress-monitoring-modal']}>
            {/* top  */}
            <img src={WarningIcon} alt={''} width={'61.5px'} height={'55px'} />
            <div className={styles['progress-monitoring-modal-top-title']}>
              {t('dashboard.my-progress.progressMonitoringConfirmation')}
            </div>
            <div className={styles['progress-monitoring-modal-top-subtitle']}>
              {' '}
              {t('dashboard.my-progress.progressMonitoringConfirmationDesc')}
            </div>
            <div className={styles['progress-monitoring-modal-input-container']}>
              <div className={styles['progress-monitoring-modal-input-container-label']}>
                {t('dashboard.my-progress.input.name')}
              </div>
              <div className={styles['progress-monitoring-modal-input-container-data']}>{decodedData.name}</div>
            </div>
            <div className={styles['progress-monitoring-modal-input-container']}>
              <div className={styles['progress-monitoring-modal-input-container-label']}>
                {t('dashboard.my-progress.input.date')}
              </div>
              <div className={styles['progress-monitoring-modal-input-container-data']}>
                {dateTimeToCustomFormat(progressRowRecId.rowObject.weighingDate)}
              </div>
            </div>

            {subKpiListDisplayInProgressMonitoring?.map((i: DynamicObjectInterface) => {
              const otherIndicators = JSON.parse(progressRowRecId.rowObject.otherIndicators);
              return (
                <div className={styles['progress-monitoring-modal-input-container']}>
                  <div className={styles['progress-monitoring-modal-input-container-label']}>
                    {i.indicatorName} ({i.indicatorMeasurementUnit})
                  </div>
                  <div className={styles['progress-monitoring-modal-input-container-data']}>
                    {otherIndicators[i.subKpiCode] ? decryptString(otherIndicators[i.subKpiCode]) : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </Modal>
      )}
    </>
  );

  const getButtonConfig = useMemo(() => {
    let commonConfig = {
      iconName: 'pencil' as IconName,
      iconStyle: IconStyle.SOLID,
      theme: Theme.GRAY,
      title: t('dashboard.my-progress.button.edit'),
    };
    return {
      result: [
        {
          ...commonConfig,
          theme: data?.teamIncomplete === true ? Theme.GRAY : Theme.WHITE,
          onClick: () => {
            setResultInitialTab('weightIn');
            toggleEditModal(Sections.Result);
          },
          disabled: isCampaignEnd,
        },
      ],
    } as { [index: string]: IconButtonProps[] };
  }, [data]);

  const toggleEditModal = (section: (typeof Sections)[keyof typeof Sections]) => {
    setShowEdit(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const onSaveParticipantDetails = async (formData: UserData) => {
    const activeSection = Object.entries(showEdit)?.find(entry => entry[1] === true)?.[0] as
      | (typeof Sections)[keyof typeof Sections]
      | undefined;

    const updatedFormData = {
      ...formData,
      campaignId: campaignData[index]?.campaignId,
      teamId: data?.teamId,
      weightIn: {
        ...formData.weightIn,
        weightedDate: formData?.weightIn?.weightedDate
          ? dateTimeToCustomFormat(formData.weightIn.weightedDate?.toString(), 'YYYY-MM-DD')
          : null,
        acknowledgeDateTime: formData?.weightIn?.acknowledgeDateTime
          ? dateTimeToCustomFormat(formData.weightIn.acknowledgeDateTime?.toString(), 'YYYY-MM-DD')
          : null,
        // Persist the selected test device (stored on weightMachine by the BE).
        machine: formData?.weightIn?.machine ?? data?.weightIn?.machine ?? null,
        otherIndicators: Object.entries(formData?.weightIn?.otherIndicators || {}).reduce(
          (acc, [key, value]) => {
            acc[key] = value ? encryptString(value.toString()) : null;
            return acc;
          },
          {} as Record<string, string | null>,
        ),
      },
      weightOut: {
        ...formData.weightOut,
        weightedDate: formData?.weightOut?.weightedDate
          ? dateTimeToCustomFormat(formData?.weightOut?.weightedDate?.toString(), 'YYYY-MM-DD')
          : null,
        acknowledgeDateTime: formData?.weightOut?.acknowledgeDateTime
          ? dateTimeToCustomFormat(formData?.weightOut?.acknowledgeDateTime?.toString(), 'YYYY-MM-DD')
          : null,
        // Post-assessment is locked to the device chosen at pre-assessment.
        machine: data?.weightIn?.machine ?? formData?.weightIn?.machine ?? formData?.weightOut?.machine ?? null,
        otherIndicators: Object.entries(formData?.weightOut?.otherIndicators || {}).reduce(
          (acc, [key, value]) => {
            acc[key] = value ? encryptString(value.toString()) : null;
            return acc;
          },
          {} as Record<string, string | null>,
        ),
      },
    };

    const payload = new FormData();
    payload.append('payload', JSON.stringify(updatedFormData));

    const saveConfigJson = data?.campaign?.configJson;
    const saveFileUploader =
      (saveConfigJson?.campaignMode === 'MULTI' && data?.subChallengeId
        ? saveConfigJson?.activities?.subChallenges?.find(
            (sc: DynamicObjectInterface) => sc.subChallengeId === data?.subChallengeId,
          )?.fileUploader
        : undefined) ?? saveConfigJson?.activities?.participant?.fileUploader;

    saveFileUploader?.forEach((i: DynamicObjectInterface) => {
      for (let idx = 0; idx < i.numberOfFile; idx++) {
        const weightInFileList = updatedFormData.weightIn?.file?.[`${i.fieldCode}_${idx}`] ?? [];
        const weightOutFileList = updatedFormData.weightOut?.file?.[`${i.fieldCode}_${idx}`] ?? [];

        weightInFileList.forEach(({ file }) => {
          if (file) {
            payload.append(`photoId.0,before_result_${i.fieldCode}_${idx}`, file);
          }
        });

        weightOutFileList.forEach(({ file }) => {
          if (file) {
            payload.append(`photoId.0,after_result_${i.fieldCode}_${idx}`, file);
          }
        });
      }
    });

    const beforeAndAfterImage = processBeforeAfterImagesData(
      Object.fromEntries(Object.entries(images).filter(([key, value]) => !!value?.file?.name)),
      campaignData[index]?.campaignId,
      String(data?.teamId),
      decodedData.userId,
    );

    for (const [key, value] of beforeAndAfterImage.entries()) {
      payload.append(key, value);
    }

    const result = await apiCall(
      ParticipantService.updateParticipantDetails,
      payload,
      activeSection ?? '',
      // searchParams?.userId!,
    );

    if (result.status === 200) {
      if (!result.data.status) {
        adminToast({
          message: getTranslatedMessage(t, result?.data?.message) || t('dashboard.my-progress.updateFail'),
          theme: Theme.ERROR,
        });
      } else {
        adminToast({
          message: t('dashboard.my-progress.updateSuccess'),
          theme: Theme.SUCCESS,
        });

        await fetchData();
        await fetchMeasurementImage();
        toggleEditModal(activeSection as (typeof Sections)[keyof typeof Sections]);
      }
    } else {
      adminToast({
        message: t('dashboard.my-progress.updateFail'),
        theme: Theme.ERROR,
      });
    }
  };

  const showModal = (
    <>
      {showEdit.weightDetails && (
        <ResultModal
          data={data}
          initialTab={resultInitialTab}
          onSave={onSaveParticipantDetails}
          onCancel={() => {
            toggleEditModal(Sections.Result);
            setFormData(data);
            data?.images && setImages(data?.images);
          }}
          formData={formData}
          handleOnChange={handleOnChange}
          campaign={campaignData[index]}
          images={images}
          handleFileImage={handleFileImage}
        />
      )}
    </>
  );

  const renderAssessmentDiff = (i: DynamicObjectInterface) => {
    const weightInValue = data?.weightIn?.otherIndicators?.[i.subKpiCode];
    const weightOutValue = data?.weightOut?.otherIndicators?.[i.subKpiCode];

    const weightOutDiffValues =
      weightInValue !== null && weightOutValue !== null
        ? i?.disablePreAssessment === 1 || i?.ignorePreAssessment === 1
          ? getRoundedNumber(weightOutValue - parseFloat(i.targetFrom))
          : i?.percentageCalculation === 1
            ? getRoundedNumber(((weightOutValue - weightInValue) / weightInValue) * 100)
            : getRoundedNumber(weightOutValue - weightInValue)
        : 0;

    if (!(weightOutValue && weightOutDiffValues && data?.weightOut?.weightedDate)) return null;

    return (
      <span
        className={`${
          i.indicatorWinningCriteria === 'gain' ? styles['difference-label-alt'] : styles['difference-label']
        } ${
          weightOutDiffValues > 0 ? styles.positive : weightOutDiffValues === 0 ? styles.zero : styles.negative
        }`}
      >
        ({weightOutDiffValues > 0 ? '+' : ''}
        {weightOutDiffValues?.toFixed(1)}
        {i?.percentageCalculation === 1 ? '%' : ''})
      </span>
    );
  };

  const assessmentTable = (
    <div className={styles['assessment-table']}>
      <div className={`${styles['assessment-row']} ${styles['assessment-row-head']}`}>
        <div className={styles['assessment-cell-metric']}>{t('dashboard.my-progress.metricLabel')}</div>
        <div className={styles['assessment-cell']}>{t('dashboard.my-progress.preAssessment')}</div>
        <div className={styles['assessment-cell']}>{t('dashboard.my-progress.postAssessment')}</div>
      </div>
      <div className={styles['assessment-row']}>
        <div className={styles['assessment-cell-metric']}>{t('dashboard.my-progress.input.date')}</div>
        <div className={styles['assessment-cell']}>
          {data?.weightIn?.weightedDate ? (
            <Tag
              theme={Theme.GRAY}
              text={dateTimeToCustomFormat(data?.weightIn?.weightedDate ?? '', 'DD/MM/yyyy')}
              style={TagStyle.OUTLINE}
            />
          ) : (
            '-'
          )}
        </div>
        <div className={styles['assessment-cell']}>
          {data?.weightOut?.weightedDate ? (
            <Tag
              theme={Theme.GRAY}
              text={dateTimeToCustomFormat(data?.weightOut?.weightedDate ?? '', 'DD/MM/yyyy')}
              style={TagStyle.OUTLINE}
            />
          ) : (
            '-'
          )}
        </div>
      </div>
      {displaySubKpiCode?.map((i: DynamicObjectInterface) => (
        <div key={i.subKpiCode} className={styles['assessment-row']}>
          <div className={styles['assessment-cell-metric']}>
            <span className={styles['label-tooltip']}>
              {i.indicatorName} ({i.indicatorMeasurementUnit})
            </span>
          </div>
          <div className={styles['assessment-cell']}>{data?.weightIn?.otherIndicators?.[i.subKpiCode] ?? '-'}</div>
          <div className={styles['assessment-cell']}>
            <div className={styles['assessment-value-wrap']}>
              <span>{data?.weightOut?.otherIndicators?.[i.subKpiCode] ?? '-'}</span>
              {renderAssessmentDiff(i)}
            </div>
          </div>
        </div>
      ))}
      <div className={styles['assessment-row']}>
        <div className={styles['assessment-cell-metric']}>{t('dashboard.my-progress.input.remarks')}</div>
        <div className={styles['assessment-cell']}>{data?.weightIn?.remarks ?? '-'}</div>
        <div className={styles['assessment-cell']}>{data?.weightOut?.remarks ?? '-'}</div>
      </div>
    </div>
  );

  // --- V2 (Figma) assessment section: configurable title, pencil-square Edit link, black-header table ---
  // Columns: Metric | [Scan Type] | Pre | Post | Change. Scan Type only when a device is present.
  const assessmentColumns: ColumnsInterface[] = [
    {
      key: 'metric',
      text: t('dashboard.my-progress.metricLabel'),
      width: 20,
      render: row => row.metric,
    },
    ...(showScanType
      ? [
          {
            key: 'scanType',
            text: t('dashboard.my-progress.scanTypeLabel'),
            width: 20,
            render: (row: DynamicObjectInterface) => row.scanType ?? '',
          },
        ]
      : []),
    {
      key: 'pre',
      text: t('dashboard.my-progress.preAssessment'),
      width: 20,
      render: row => row.pre ?? '-',
    },
    {
      key: 'post',
      text: t('dashboard.my-progress.postAssessment'),
      width: 20,
      render: row => row.post ?? '-',
    },
    // Only show the change/difference column once the post-assessment is done —
    // otherwise it's an empty column. weightOut.weightedDate = post submitted.
    ...(data?.weightOut?.weightedDate
      ? [
          {
            key: 'change',
            text: '',
            width: 20,
            render: (row: DynamicObjectInterface) => row.change ?? '',
          },
        ]
      : []),
  ];

  const assessmentDataSource: DynamicObjectInterface[] = [
    {
      key: 'date',
      metric: t('dashboard.my-progress.input.date'),
      scanType: '',
      pre: data?.weightIn?.weightedDate
        ? dateTimeToCustomFormat(data?.weightIn?.weightedDate ?? '', 'D MMM YYYY')
        : '-',
      post: data?.weightOut?.weightedDate
        ? dateTimeToCustomFormat(data?.weightOut?.weightedDate ?? '', 'D MMM YYYY')
        : '-',
      change: '',
    },
    ...(displaySubKpiCode?.map((i: DynamicObjectInterface) => {
      const change = getAssessmentChange(i);
      return {
        key: i.subKpiCode,
        metric: (
          <span className={styles['label-tooltip']}>
            {i.indicatorName} ({i.indicatorMeasurementUnit})
          </span>
        ),
        scanType: selectedDeviceName,
        pre: data?.weightIn?.otherIndicators?.[i.subKpiCode] ?? '-',
        post: data?.weightOut?.otherIndicators?.[i.subKpiCode] ?? '-',
        change: change ? (
          change.winLose ? (
            // Win/lose indicators (gender-age threshold, disable/ignore pre) have no
            // meaningful numeric diff — show a green tick (win) or red cross (lose).
            <FAIcon
              iconStyle={IconStyle.SOLID}
              iconName={change.achieved ? 'check' : 'xmark'}
              theme={change.achieved ? Theme.SUCCESS : Theme.ERROR}
            />
          ) : (
            <span
              className={`${styles['difference-label']} ${change.achieved ? styles['change-achieved'] : styles['change-missed']}`}
            >
              {change.text}
            </span>
          )
        ) : (
          ''
        ),
      };
    }) ?? []),
    {
      key: 'remarks',
      metric: t('dashboard.my-progress.input.remarks'),
      scanType: '',
      pre: data?.weightIn?.remarks ?? '-',
      post: data?.weightOut?.remarks ?? '-',
      change: '',
    },
  ];

  const canEditAssessment =
    !readOnly &&
    (data?.campaign?.campaignEnd
      ? new Date(new Date(data?.campaign?.campaignEnd)?.getTime()).setHours(0, 0, 0, 0) >=
          currentDate.setHours(0, 0, 0, 0) && data?.campaign?.configJson?.campaignStatus !== 'CLOSED'
      : true);

  // Check-in fieldCodes used to key uploaded files in the `files` state map.
  const checkInFileFields = useMemo(
    () => (checkInConfig.fileUploader ?? []).map((f: DynamicObjectInterface) => `checkin_${f.fieldCode}`),
    [checkInConfig],
  );

  const openCheckInModal = (day: number) => {
    setCheckInDay(day);
    setCheckInRemarks('');
    // Clear any previously staged check-in files.
    setFiles(prev => {
      const next = { ...prev };
      checkInFileFields.forEach(fc => delete next[fc]);
      return next;
    });
    setProgress(prev => {
      const next = { ...prev };
      checkInFileFields.forEach(fc => delete next[fc]);
      return next;
    });
  };

  const closeCheckInModal = () => {
    setCheckInDay(null);
    setCheckInRemarks('');
  };

  // Download a submitted check-in proof file by its attachment recId (same blob flow
  // as the assessment edit modal's thumbnail download).
  const handleDownloadCheckInFile = async (photoId: number, fileName: string) => {
    try {
      const response = await apiCall(AttachmentService.downloadSelected, { recId: photoId });
      if (response.status === 200 && !response.data?.data?.message) {
        const blob = response.data as Blob;
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', fileName || 'attachment');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      } else {
        adminToast({ message: response?.data?.data?.message, theme: Theme.ERROR });
      }
    } catch (error) {
      LoggerService.log('Error downloading check-in attachment', error);
    }
  };

  // Every configured check-in fileUploader field must have the required number of files.
  const checkInFilesComplete = (checkInConfig.fileUploader ?? []).every((f: DynamicObjectInterface) => {
    const required = Number(f.numberOfFile) || 1;
    return (files[`checkin_${f.fieldCode}`]?.length ?? 0) >= required;
  });

  const handleSaveCheckIn = async () => {
    if (checkInDay == null) return;
    setCheckInSubmitting(true);
    try {
      // 1) Collect the selected proof files (fieldCode + index + File).
      const selectedFiles: { fieldCode: string; idx: number; file: File }[] = [];
      (checkInConfig.fileUploader ?? []).forEach((f: DynamicObjectInterface) => {
        (files[`checkin_${f.fieldCode}`] ?? []).forEach((fileObj, idx) => {
          if (fileObj.file) selectedFiles.push({ fieldCode: f.fieldCode, idx, file: fileObj.file });
        });
      });

      // 2) Ask the BFF for presigned S3 PUT URLs (JSON only — WAF-safe).
      let attachments: { photoType: string; key: string; fileType?: string }[] = [];
      if (selectedFiles.length > 0) {
        const urlRes = await apiCall(AttachmentService.getCheckInUploadUrls, {
          loginId: decodedData.loginId,
          userId: data?.userId ?? decodedData.userId,
          partyId: decodedData.partyId,
          campaignId: campaignData[index]?.campaignId,
          teamId: data?.teamId,
          day: checkInDay,
          files: selectedFiles.map(s => ({
            fieldCode: s.fieldCode,
            idx: s.idx,
            fileName: s.file.name,
            contentType: s.file.type,
          })),
        });

        const urls: DynamicObjectInterface[] = urlRes?.data?.data ?? [];
        if (!urls.length) {
          adminToast({ message: t('dashboard.my-progress.updateFail'), theme: Theme.ERROR });
          return;
        }

        // 3) PUT each file directly to S3 (bypasses BFF/CloudFront WAF).
        for (const u of urls) {
          const match = selectedFiles.find(s => s.fieldCode === u.fieldCode && s.idx === u.idx);
          if (!match) continue;
          const ok = await AttachmentService.putToPresignedUrl(u.uploadUrl, match.file);
          if (!ok) {
            adminToast({ message: t('dashboard.my-progress.updateFail'), theme: Theme.ERROR });
            return;
          }
          attachments.push({ photoType: u.photoType, key: u.key, fileType: u.fileType });
        }
      }

      // 4) Record the check-in with only the S3 keys (no binary in the body).
      const payload = new FormData();
      payload.append(
        'payload',
        JSON.stringify({
          loginId: decodedData.loginId,
          userId: data?.userId ?? decodedData.userId,
          partyId: decodedData.partyId,
          campaignId: campaignData[index]?.campaignId,
          teamId: data?.teamId,
          name: data?.name,
          checkIn: {
            day: checkInDay,
            remarks: checkInRemarks,
            checkInDate: dateTimeToCustomFormat(new Date().toString(), 'YYYY-MM-DD'),
            attachments,
          },
        }),
      );

      const result = await apiCall(ParticipantService.updateParticipantDetails, payload, 'checkIn');

      if (result.status === 200 && result.data?.status !== false) {
        adminToast({ message: t('dashboard.my-progress.checkInSuccess'), theme: Theme.SUCCESS });
        closeCheckInModal();
        await fetchData(selectedTeamId);
      } else {
        adminToast({
          message: getTranslatedMessage(t, result?.data?.message) || t('dashboard.my-progress.updateFail'),
          theme: Theme.ERROR,
        });
      }
    } catch (error) {
      LoggerService.log('Failed to submit check-in', error);
      adminToast({ message: t('dashboard.my-progress.updateFail'), theme: Theme.ERROR });
    } finally {
      setCheckInSubmitting(false);
    }
  };

  const checkInModalBody = (
    <div className={styles['checkin-modal']}>
      <div className={styles['checkin-modal-desc']}>
        {t('dashboard.my-progress.checkInModalDesc').replace('{day}', `${checkInDay ?? ''}`)}
      </div>
      {(checkInConfig.fileUploader ?? []).map((f: DynamicObjectInterface) => {
        const fieldCode = `checkin_${f.fieldCode}`;
        return (
          <div key={fieldCode} className={styles['checkin-modal-upload']}>
            <div className={styles['checkin-modal-upload-label']}>{f.fieldName}</div>
            <VideoEditor
              onFileUpload={e => handleFile(e, fieldCode)}
              accept="image/*, application/pdf"
              fileTypeLabel={t('dashboard.my-progress.allowedFileType')}
              multiple={Number(f.numberOfFile) > 1}
              files={files[fieldCode] ?? []}
              onDelete={i => handleDelete(i, fieldCode)}
              disabled={false}
              progress={progress[fieldCode] ?? []}
              dragCTALabel={t('dashboard.my-progress.dragAndDrop')}
              connectorLabel={t('dashboard.my-progress.or')}
              uploadCTALabel={t('dashboard.my-progress.selectFileToUpload')}
              addAnotherFile={t('dashboard.my-progress.addAnotherFile')}
            />
          </div>
        );
      })}
      {(checkInConfig.fileUploader ?? []).length > 0 && (
        <div className={styles['checkin-modal-desc']}>
          {t('dashboard.my-progress.assessmentUploadFileSizeDesc')}
        </div>
      )}
      <Input
        type={InputTypes.TEXT}
        name="checkInRemarks"
        label={t('dashboard.my-progress.input.remarks')}
        mlpVersion={MLPVersion.ONE}
        value={checkInRemarks}
        onChange={e => setCheckInRemarks(e.target.value)}
      />
    </div>
  );

  const checkInModal = checkInDay != null && (
    isDesktopView ? (
      <Modal
        headline={t('dashboard.my-progress.submitCheckIn')}
        textAlign={TextAlign.LEFT}
        containerWidth="600px"
        closeIcon={false}
        primaryButton={
          <Button
            title={t('dashboard.my-progress.button.confirm')}
            type={ButtonTypes.BUTTON}
            theme={Theme.GREEN}
            buttonStyle={ButtonStyles.SOLID}
            size={ButtonSize.MEDIUM}
            onClick={handleSaveCheckIn}
            disabled={!checkInFilesComplete || checkInSubmitting}
          />
        }
        secondaryButton={
          <Button
            title={t('dashboard.my-progress.button.cancel')}
            type={ButtonTypes.BUTTON}
            theme={Theme.GREEN}
            buttonStyle={ButtonStyles.OUTLINE}
            size={ButtonSize.MEDIUM}
            onClick={closeCheckInModal}
          />
        }
      >
        {checkInModalBody}
      </Modal>
    ) : (
      <Drawer
        onClose={closeCheckInModal}
        header={t('dashboard.my-progress.submitCheckIn')}
        closeOnBackdropClick={true}
        bottomGap={0}
        footer={
          <div className={styles['drawer-buttons']}>
            <Button
              title={t('dashboard.my-progress.button.cancel')}
              type={ButtonTypes.BUTTON}
              theme={Theme.GREEN}
              buttonStyle={ButtonStyles.OUTLINE}
              size={ButtonSize.MEDIUM}
              onClick={closeCheckInModal}
            />
            <Button
              title={t('dashboard.my-progress.button.confirm')}
              type={ButtonTypes.BUTTON}
              theme={Theme.GREEN}
              buttonStyle={ButtonStyles.SOLID}
              size={ButtonSize.MEDIUM}
              onClick={handleSaveCheckIn}
              disabled={!checkInFilesComplete || checkInSubmitting}
            />
          </div>
        }
      >
        {checkInModalBody}
      </Drawer>
    )
  );

  const checkInColumns: ColumnsInterface[] = [
    {
      key: 'day',
      text: t('dashboard.my-progress.checkInDayColumn'),
      render: row => row.dayLabel,
    },
    {
      key: 'attachment',
      text: t('dashboard.my-progress.checkInAttachmentColumn'),
      render: row => row.attachment,
    },
    {
      key: 'status',
      text: t('dashboard.my-progress.checkInStatusColumn'),
      render: row => row.status,
    },
    {
      key: 'action',
      text: t('dashboard.my-progress.checkInActionColumn'),
      render: row => row.action,
    },
  ];

  const checkInDataSource: DynamicObjectInterface[] = checkInDays.map(day => {
    const submitted = submittedCheckInDays.has(day);
    const open = dayIsOpen(day);

    // complete (green) / unlocked (orange) / locked (gray)
    const statusTag = submitted ? (
      <Tag text={t('dashboard.my-progress.completedLabel')} theme={Theme.SUCCESS} style={TagStyle.SOLID} />
    ) : open ? (
      <Tag text={t('dashboard.my-progress.unlockedLabel')} theme={Theme.WARNING} style={TagStyle.SOLID} />
    ) : (
      <Tag text={t('dashboard.my-progress.lockedLabel')} theme={Theme.GRAY} style={TagStyle.SOLID} />
    );

    const canAct = !readOnly && open && !submitted && !teamLocked && !isCampaignEnd && !data?.teamIncomplete;

    // Uploaded proof files for this day → clickable download links (like the
    // assessment edit-modal thumbnails). Falls back to "No attachment".
    const dayFiles = checkInFiles[day] ?? [];
    const attachment =
      dayFiles.length > 0 ? (
        <div className={styles['checkin-attachment-links']}>
          {dayFiles.map((f, idx) => (
            <span
              key={`${day}-${f.photoId}-${idx}`}
              className={styles['checkin-attachment-link']}
              role="button"
              tabIndex={0}
              onClick={() => handleDownloadCheckInFile(f.photoId, f.fileName)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleDownloadCheckInFile(f.photoId, f.fileName);
                }
              }}
            >
              {f.fileName}
            </span>
          ))}
        </div>
      ) : (
        t('dashboard.my-progress.checkInNoAttachment')
      );

    return {
      key: `checkin-${day}`,
      dayLabel: `${t('dashboard.my-progress.checkInDayLabel')} ${day}`,
      attachment,
      status: statusTag,
      action: canAct ? (
        <TextLink
          text={t('dashboard.my-progress.continueTask')}
          theme={ThemeV2.BLUE}
          textLinkStyle={TextLinkStyle.ICON}
          leftIcon={{ iconName: 'list-check', iconStyle: IconStyleV2.SOLID }}
          onClick={() => openCheckInModal(day)}
        />
      ) : (
        '-'
      ),
    };
  });

  const checkInSection = checkInConfigured && (
    <div className={styles['assessment-section']}>
      <div className={styles['assessment-section-head']}>
        <div className={styles['assessment-section-title']}>{t('dashboard.my-progress.stepCheckIn')}</div>
      </div>
      <div className={styles['my-progress-desc']}>{t('dashboard.my-progress.checkInTableDesc')}</div>
      {/* Table on web/tablet, pivoted cards on phone (toggled via CSS). */}
      <div className={styles['hide-on-mobile']}>
        <TableV2
          theme={ThemeV2.BLACK}
          tableVersion={TableVersion.ADMIN}
          mlpVersion={MLPVersionV2.THREE}
          pagination={false}
          rowKey="key"
          columns={checkInColumns}
          dataSource={checkInDataSource}
        />
      </div>
      <div className={styles['assessment-cards']}>
        {checkInDataSource.map(row => (
          <div key={row.key} className={styles['assessment-card']}>
            {/* Day heading with the action (Continue Task) pinned to the opposite end,
                so it stays visible on the first line and the details below stay short. */}
            <div className={`${styles['assessment-card-metric']} ${styles['assessment-card-metric-action']}`}>
              <span>{row.dayLabel}</span>
              {row.action && row.action !== '-' && <span>{row.action}</span>}
            </div>
            <div className={styles['assessment-card-row']}>
              <span className={styles['assessment-card-label']}>
                {t('dashboard.my-progress.checkInAttachmentColumn')}
              </span>
              <span className={styles['assessment-card-value']}>{row.attachment}</span>
            </div>
            <div className={styles['assessment-card-row']}>
              <span className={styles['assessment-card-label']}>{t('dashboard.my-progress.checkInStatusColumn')}</span>
              <span className={styles['assessment-card-value']}>{row.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Before/After comparison sliders, paired by view: Front (before_front -> after_front)
  // and Side (before_side -> after_side). Bound to the `images` state.
  const assessmentPhotos = resolveProfileImageSectionShown(data?.campaign?.configJson, data?.subChallengeId) && (
    <div className={styles['assessment-photos']}>
      <BeforeAfterSlider
        beforeUrl={images?.['before_front']?.url || undefined}
        afterUrl={images?.['after_front']?.url || undefined}
        fallbackUrl={PhotoUnavailable}
        caption={t('dashboard.my-progress.photoFront')}
        beforeLabel={t('dashboard.my-progress.photoBefore')}
        afterLabel={t('dashboard.my-progress.photoAfter')}
        alt="Front View"
      />
      <BeforeAfterSlider
        beforeUrl={images?.['before_side']?.url || undefined}
        afterUrl={images?.['after_side']?.url || undefined}
        fallbackUrl={PhotoUnavailable}
        caption={t('dashboard.my-progress.photoSide')}
        beforeLabel={t('dashboard.my-progress.photoBefore')}
        afterLabel={t('dashboard.my-progress.photoAfter')}
        alt="Side View"
      />
    </div>
  );

  // Mobile-only pivoted view of the assessment table: one card per row (metric),
  // with Pre/Post/Change stacked as labeled lines. The table itself is too wide for
  // phones. Reuses the same assessmentDataSource so values/logic stay in one place.
  // Shown only under xs-mobile via CSS; the table is hidden on phones (and vice versa),
  // so web/iPad are unchanged.
  const showChangeInCards = !!data?.weightOut?.weightedDate;
  const assessmentCards = (
    <div className={styles['assessment-cards']}>
      {/* Scan Type is the same device for every metric, so show it once at the top
          instead of repeating it in every card. */}
      {showScanType && (
        <div className={styles['assessment-cards-scan-type']}>
          <span className={styles['assessment-card-label']}>{t('dashboard.my-progress.scanTypeLabel')}</span>
          <span className={styles['assessment-card-value']}>{selectedDeviceName || '-'}</span>
        </div>
      )}
      {assessmentDataSource.map(row => (
        <div key={row.key} className={styles['assessment-card']}>
          <div className={styles['assessment-card-metric']}>{row.metric}</div>
          <div className={styles['assessment-card-row']}>
            <span className={styles['assessment-card-label']}>{t('dashboard.my-progress.preAssessment')}</span>
            <span className={styles['assessment-card-value']}>{row.pre ?? '-'}</span>
          </div>
          <div className={styles['assessment-card-row']}>
            <span className={styles['assessment-card-label']}>{t('dashboard.my-progress.postAssessment')}</span>
            <span className={styles['assessment-card-value']}>{row.post ?? '-'}</span>
          </div>
          {showChangeInCards && row.change && (
            <div className={styles['assessment-card-row']}>
              <span className={styles['assessment-card-label']}>{t('dashboard.my-progress.changeLabel')}</span>
              <span className={styles['assessment-card-value']}>{row.change}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const assessmentSection = (
    <Card containerHeight="100%">
      <div className={styles['assessment-section']}>
        <div className={styles['assessment-section-head']}>
          <div className={styles['assessment-section-title']}>{assessmentTitle}</div>
          {canEditAssessment && !data?.teamIncomplete && !isCampaignEnd && (
            // Once the post-assessment is submitted the modal is read-only (see
            // AssessmentDetail), so label the link "View" instead of "Edit" to avoid
            // implying the results can still be changed.
            <TextLink
              text={
                memberProgress.isAllWeightOut
                  ? t('dashboard.my-progress.button.view')
                  : t('dashboard.my-progress.button.edit')
              }
              theme={ThemeV2.BLUE}
              textLinkStyle={TextLinkStyle.ICON}
              leftIcon={{
                iconName: memberProgress.isAllWeightOut ? 'eye' : 'pen-to-square',
                iconStyle: IconStyleV2.REGULAR,
              }}
              onClick={() => {
                setResultInitialTab('weightIn');
                toggleEditModal(Sections.Result);
              }}
            />
          )}
        </div>
        {/* Team-incomplete banner (mirrors the old measurementDetail), shown above
            the assessment table so participants know the challenge hasn't started. */}
        {data?.teamIncomplete && (
          <div className={styles['team-notification']}>
            <img
              src={Error}
              width={'32px'}
              height={'32px'}
              className={styles['team-notification-icon']}
              alt={'warning'}
            />
            <div>{t('dashboard.my-team.label.incompleteTeamNotification')}</div>
          </div>
        )}
        {/* Table on web/tablet, pivoted cards on phone (toggled via CSS). */}
        <div className={`${styles['assessment-table-wrapper']} ${styles['hide-on-mobile']}`}>
          <TableV2
            theme={ThemeV2.BLACK}
            tableVersion={TableVersion.ADMIN}
            mlpVersion={MLPVersionV2.THREE}
            pagination={false}
            rowKey="key"
            columns={assessmentColumns}
            dataSource={assessmentDataSource}
          />
        </div>
        {assessmentCards}
        {assessmentPhotos}
        {checkInSection}
      </div>
    </Card>
  );

  // measurementDetail is old ui assessment table
  const measurementDetail = (
    <Card
      title={assessmentTitle}
      headerStyle={isDesktopView ? HeaderStyle.HEADER_5 : HeaderStyle.HEADER_6}
      buttonType={ButtonType.ICON}
      button={
        readOnly
          ? undefined
          : data?.campaign?.campaignEnd
            ? new Date(new Date(data?.campaign?.campaignEnd)?.getTime()).setHours(0, 0, 0, 0) >=
                currentDate.setHours(0, 0, 0, 0) && data?.campaign?.configJson?.campaignStatus !== 'CLOSED'
              ? getButtonConfig.result
              : undefined
            : getButtonConfig.result
      }
    >
      {data?.teamIncomplete && (
        <div className={styles['team-notification']}>
          <img
            src={Error}
            width={'32px'}
            height={'32px'}
            className={styles['team-notification-icon']}
            alt={'warning'}
          />
          <div>{t('dashboard.my-team.label.incompleteTeamNotification')}</div>
        </div>
      )}
      {assessmentPhotos}
      {assessmentTable}
      {data?.weightIn?.weightedDate && data?.campaign?.configJson?.campaignIsProgressMonitoringSectionShown === 1 && (
        <>
          <hr className={styles['progress-monitoring-line']} />
          {/* interim measurement  */}
          <div className={styles['progress-monitoring']}>
            <div className={styles['progress-monitoring-title-container']}>
              <div className={styles['progress-monitoring-title-container-title']}>
                {t('dashboard.my-progress.progressMonitoringTitle')}
              </div>
              <div className={styles['progress-monitoring-title-container-subtitle']}>
                {t('dashboard.my-progress.optional')}
              </div>
              <Tooltip
                message={t('dashboard.my-progress.progressMonitoringTooltip')}
                preference={TooltipPreference.RIGHT}
                className={styles['input-tool-tip']}
                iconName="circle-exclamation"
              />
            </div>
            <div>{t('dashboard.my-progress.progressMonitoringDesc')}</div>
            {!readOnly && subKpiListDisplayInProgressMonitoring?.length > 0 && (
              <div className={styles['progress-monitoring-log-container']}>
                <button
                  onClick={() => setOpenProgressModal(true)}
                  className={styles['progress-monitoring-button-section']}
                >
                  <img src={CirclePlusGreen} alt="" />
                  <div className={styles['progress-monitoring-log-text']}>
                    {t('dashboard.my-progress.progressMonitoringAddNew')}
                  </div>
                </button>
              </div>
            )}
            {progressData?.length > 0 && (
              <TableWithAction
                actionColumnWidth={isDesktopView ? 20 : 5}
                enableCheckbox={false}
                dataSource={progressData ?? []}
                columns={progressMonitorConfig}
                pagination={true}
                rowKey={'recId'}
                menuItems={[]}
                iconProps= {[
                  {
                    iconName: 'download',
                    iconStyle: IconStyle.SOLID,
                    isDisabled: (row) => row?.fileAvailable === 0,
                    onIconClick: (row) => downloadIntermediateResult(row)
                  },
                  {
                    iconName: 'trash-can',
                    iconStyle: IconStyle.SOLID,
                    isDisabled: readOnly,
                    onIconClick: (row, index) => deleteProgressMonitorRec(row, index),
                  },
                ]}
                actionType={ActionType.TWO_ACTION_ICON}
              />
            )}

            {addProgress}
            {deleteProgress}
          </div>
        </>
      )}
    </Card>
  );

  const rewardDetail = (
    <Card
      title={t('dashboard.my-progress.rewardStatusTitle')}
      headerStyle={isDesktopView ? HeaderStyle.HEADER_5 : HeaderStyle.HEADER_6}
      containerHeight="100%"
    >
      <div className={styles['outer-reward-container']}>
        {data?.activities && data?.activities.length > 0 ? (
          data.activities.every(activity => activity.reward.length === 0) ? (
            <OperationCenterErrorCard
              errorType={ErrorType.NO_REWARD_AVAILABLE}
              className={styles['team-details-opt-card']}
            />
          ) : (
            <div>{rewardGiftModal}</div>
          )
        ) : (
          <OperationCenterErrorCard
            errorType={ErrorType.NO_REWARD_AVAILABLE}
            className={styles['team-details-opt-card']}
          />
        )}
      </div>
    </Card>
  );

  const participantProgress = (
    <Card
      title={t('dashboard.my-progress.individualProgressTitle')}
      headerStyle={isDesktopView ? HeaderStyle.HEADER_5 : HeaderStyle.HEADER_6}
      containerHeight="100%"
    >
      <div className={styles['progress']}>
        <div className={styles['team-details-container']}>
          <div className={styles['team-details-title-container']}>
            <div className={styles['team-details-coach']}>
              <Tag text={`${data?.campaignName}`} theme={Theme.GRAY} style={TagStyle.SOLID} />
            </div>
            <div className={styles['team-details-coach']}>
              {data?.teamStatus && (
                <Tag
                  text={getTeamStatus(t, data?.teamStatus ?? '', '').toUpperCase()}
                  theme={data?.teamStatus === 'ACTIVE' ? Theme.SUCCESS : Theme.PURPLE}
                  style={TagStyle.OUTLINE}
                />
              )}
            </div>

            {/* {data?.teamCoachId && (
              <div className={styles['team-details-coach']}>
                <div className={styles['team-details-coach-subtitle']}>{'Team Leader'}</div>
                <Tag
                  text={`${data?.teamCoachId} - ${data?.teamCoachName}`}
                  theme={Theme.WHITE}
                  style={TagStyle.OUTLINE}
                />
              </div>
            )} */}
          </div>
          <div className={styles['']}>
            {data?.participationType !== 'ind' && (
              <div className={styles['team-details-coach']}>
                {t('dashboard.my-progress.teamNameLabel')} :
                <div className={styles['team-details-date-range']}> {data?.teamName}</div>
              </div>
            )}
            <div className={styles['team-details-coach']}>
              {t('dashboard.my-progress.teamDateLabel')} :
              <div className={styles['team-details-date-range']}>{`${
                data?.teamStartDate ? formatDateDMYYYY(new Date(data?.teamStartDate)) : 'N/A'
              } - ${data?.teamEndDate ? formatDateDMYYYY(new Date(data?.teamEndDate)) : 'N/A'}`}</div>
            </div>
          </div>
          <div>
            <div className={styles['team-details-progress-card']}>
              {data?.participantDetails
                ?.filter(participant => participant.status === 'ACTIVE' && participant.userId === data.userId)
                .map((participant, i) => {
                  return (
                    <div key={i} className={styles['team-details-progress-card']}>
                      <DetailCardProgress
                        isCoachEnrollment={false}
                        showFiveProgress={false}
                        hideProfileImg={true}
                        {...participant}
                      />
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );

  const timelineSteps = [
    { step: TeamProgressStep.PURCHASE, label: t('dashboard.my-progress.stepProductPurchase') },
    { step: TeamProgressStep.PRE_ASSESSMENT, label: t('dashboard.my-progress.stepPreAssessment') },
    { step: TeamProgressStep.CHECK_IN, label: t('dashboard.my-progress.stepCheckIn') },
    { step: TeamProgressStep.POST_ASSESSMENT, label: t('dashboard.my-progress.stepPostAssessment') },
    { step: TeamProgressStep.RESULT, label: t('dashboard.my-progress.stepOverallResults') },
  ];

  const purchaseOrderNumber = currentMember?.purchase?.[0]?.orderNumber;

  // Next actionable step in the linear Purchase -> Pre -> Post chain. Check-In is
  // Check-In is evaluated by its own day window (not the linear flag chain), but it
  // DOES gate Post-Assessment: post stays locked until check-in is fully done.
  const checkInGateOpen = !checkInConfigured || isCheckInComplete;
  const unlockedStep: TeamProgressStep | null = !memberProgress.isAllPurchase
    ? TeamProgressStep.PURCHASE
    : // Team hasn't started yet (incomplete team, no teamStartDate): Purchase is done
      // but Pre-Assessment onward stays locked until the team starts.
      teamLocked
      ? null
      : !memberProgress.isAllWeightIn
        ? TeamProgressStep.PRE_ASSESSMENT
        : // Post-Assessment only "unlocks" once its time window is open (same gate as
          // the AssessmentDetail form); before that it stays locked, not unlocked.
          !memberProgress.isAllWeightOut && checkInGateOpen && postAssessmentWindowOpen
          ? TeamProgressStep.POST_ASSESSMENT
          : null;

    // 'failed'     = Overall Results reached but winning criteria not met (red cross,
    //                "Challenge Completed").
    // 'incomplete' = the campaign has ended with this step still not done, so it can
    //                no longer be worked on (red cross, "Incomplete").
  type StepStatus = 'complete' | 'unlocked' | 'locked' | 'failed' | 'incomplete';
  const stepStatus = (step: TeamProgressStep): StepStatus => {
    // Base status (ignores campaign-end).
    const baseStatus = (): StepStatus => {
      // Check-In is independent: complete when all days done, unlocked once a day's
      // window has opened (and not all done), otherwise locked.
      if (step === TeamProgressStep.CHECK_IN) {
        if (!checkInConfigured) return 'locked';
        if (isCheckInComplete) return 'complete';
        return nextOpenCheckInDay != null ? 'unlocked' : 'locked';
      }
      // Overall Results = journey done once Post-Assessment is submitted. Completed
      // but not won => 'failed' (red cross); won => 'complete' (green tick).
      if (step === TeamProgressStep.RESULT) {
        if (!memberProgress.isAllWeightOut) return 'locked';
        return memberProgress.isAllWin ? 'complete' : 'failed';
      }
      const state = memberProgress.steps.find(s => s.step === step);
      if (state?.complete === true) return 'complete';
      if (step === unlockedStep) return 'unlocked';
      return 'locked';
    };

    const status = baseStatus();
    // Once the campaign has ended, any step that isn't complete (and isn't already the
    // not-won 'failed' result) can no longer be actioned — mark it 'incomplete' so the
    // tracker shows a red cross + "Incomplete" instead of "Unlocked"/"Locked".
    if (isCampaignEnd && status !== 'complete' && status !== 'failed') {
      return 'incomplete';
    }
    return status;
  };

  const stepStatusText = (step: TeamProgressStep): string => {
    const status = stepStatus(step);
    // Campaign ended with this step not done — can no longer be worked on.
    if (status === 'incomplete') return t('dashboard.my-progress.incompleteLabel');
    if (step === TeamProgressStep.CHECK_IN && checkInConfigured) {
      if (status === 'complete') return t('dashboard.my-progress.completedLabel');
      if (status === 'unlocked') return t('dashboard.my-progress.unlockedLabel');
      return t('dashboard.my-progress.lockedLabel');
    }
    // Overall Results shows "Challenge Completed" whether won (complete) or not won
    // (failed) — only the icon colour differs (green tick vs red cross).
    if (step === TeamProgressStep.RESULT && (status === 'complete' || status === 'failed')) {
      return t('dashboard.my-progress.challengeCompletedLabel');
    }
    if (status === 'complete') {
      if (step === TeamProgressStep.PURCHASE) return `#${purchaseOrderNumber ?? ''}`;
      // Pre/Post-Assessment (and any other completed step) read a plain "Completed".
      return t('dashboard.my-progress.completedLabel');
    }
    if (status === 'unlocked') return t('dashboard.my-progress.unlockedLabel');
    return t('dashboard.my-progress.lockedLabel');
  };

  const myProgressSection = (
    <Card containerHeight="100%">
      <div className={styles['my-progress']}>
        <div className={styles['my-progress-heading']}>
          <div className={styles['my-progress-title']}>{t('dashboard.my-progress.myProgressTitle')}</div>
        </div>

        <div className={styles['my-progress-team-selector']}>
          <DropDown
            id="my-progress-team"
            name="my-progress-team"
            mlpVersion={MLPVersion.ONE}
            dropdownLabel={t('dashboard.my-progress.selectTeamLabel')}
            placeholder={t('dashboard.my-progress.selectTeamLabel')}
            data={teamOptions}
            disabled={readOnly}
            value={
              (viewUserId != null
                ? (teamOptions[0]?.name as string)
                : (teamOptions.find(o => `${o.id}` === `${selectedTeamId}`)?.name as string)) ?? ''
            }
            configData={{
              label: 'name',
              onSelect: (selected: DropDownData) => handleSelectTeam(`${selected.id}`),
            }}
          />
        </div>

        <div className={styles['my-progress-card']}>
          <div className={styles['my-progress-info-row']}>
            <div className={styles['my-progress-info-item']}>
              <div className={styles['my-progress-info-label']}>{t('dashboard.my-progress.challengeLabel')}</div>
              <div className={styles['my-progress-info-value']}>
                {selectedSubChallengeName || data?.teamName || '-'}
              </div>
            </div>
            <div className={styles['my-progress-info-item']}>
              <div className={styles['my-progress-info-label']}>{t('dashboard.my-progress.activePeriodLabel')}</div>
              <div className={styles['my-progress-info-value']}>{`${
                data?.teamStartDate ? formatDateDMYYYY(new Date(data?.teamStartDate)) : 'N/A'
              } - ${data?.teamEndDate ? formatDateDMYYYY(new Date(data?.teamEndDate)) : 'N/A'}`}</div>
            </div>
            <div className={styles['my-progress-info-item']}>
              <div className={styles['my-progress-info-label']}>{t('dashboard.my-progress.teamLeaderLabel')}</div>
              <div className={styles['my-progress-info-value']}>
                {data?.teamLeader
                  ? `${data?.teamLeaderLoginId ? `${data?.teamLeaderLoginId} ` : ''}${data?.teamLeader}`
                  : '-'}
                {((data?.teamLeaderUserId && `${data?.teamLeaderUserId}` === `${data?.userId}`) ||
                  (data?.teamLeaderLoginId && `${data?.teamLeaderLoginId}` === `${decodedData?.loginId}`)) && (
                  <span className={styles['my-progress-you-badge']}>{t('dashboard.my-progress.youBadge')}</span>
                )}
              </div>
            </div>
          </div>

          <div className={styles['my-progress-timeline']}>
            {timelineSteps.map((s, idx) => {
            const status = stepStatus(s.step);
            // Both 'failed' (not won) and 'incomplete' (campaign ended, not done) use
            // the red-cross marker.
            const isRedCross = status === 'failed' || status === 'incomplete';
            const markerClass =
              status === 'complete'
                ? styles['marker-complete']
                : isRedCross
                  ? styles['marker-failed']
                  : status === 'unlocked'
                    ? styles['marker-unlocked']
                    : styles['marker-locked'];
            const statusTextClass =
              status === 'complete'
                ? styles['status-complete']
                : status === 'incomplete'
                  ? styles['status-incomplete']
                  : status === 'failed'
                    ? styles['status-failed']
                    : status === 'unlocked'
                      ? styles['status-unlocked']
                      : styles['status-locked'];
            // Red cross for failed/incomplete; tick for complete; lock icons otherwise.
            const markerIcon =
              status === 'complete'
                ? 'check'
                : isRedCross
                  ? 'xmark'
                  : status === 'unlocked'
                    ? 'lock-open'
                    : 'lock';
            return (
              <Fragment key={s.step}>
                <div className={styles['my-progress-timeline-step']}>
                  <div className={`${styles['my-progress-timeline-marker']} ${markerClass}`}>
                    <FAIcon iconStyle={IconStyle.SOLID} iconName={markerIcon} theme={Theme.WHITE} />
                  </div>
                  <div className={styles['my-progress-timeline-label']}>{s.label}</div>
                  <div className={`${styles['my-progress-timeline-status']} ${statusTextClass}`}>
                    {stepStatusText(s.step)}
                  </div>
                </div>
                {idx < timelineSteps.length - 1 && (
                  <div
                    className={`${styles['my-progress-timeline-connector']} ${
                      status === 'complete' ? styles['connector-complete'] : ''
                    }`}
                  />
                )}
              </Fragment>
            );
          })}
          </div>
        </div>

        {!readOnly &&
          !teamLocked &&
          !isCampaignEnd &&
          data?.campaign?.configJson?.campaignStatus !== 'CLOSED' &&
          !data?.teamIncomplete &&
          // Once post-assessment is submitted the journey is done — hide the action entirely.
          !memberProgress.isAllWeightOut &&
          (() => {
            // Single contextual action following the journey order:
            // Pre-Assessment -> Check-In (when configured) -> Post-Assessment.
            const isPreAssessmentStep = !memberProgress.isAllWeightIn;
            const isCheckInStep = !isPreAssessmentStep && checkInConfigured && !isCheckInComplete;

            let actionButtonTitle: string;
            let onActionButtonClick: () => void;
            let isActionButtonDisabled: boolean;

            if (isPreAssessmentStep) {
              actionButtonTitle = t('dashboard.my-progress.uploadPreAssessment');
              onActionButtonClick = () => {
                setResultInitialTab('weightIn');
                toggleEditModal(Sections.Result);
              };
              isActionButtonDisabled = false;
            } else if (isCheckInStep) {
              actionButtonTitle = t('dashboard.my-progress.submitCheckIn');
              onActionButtonClick = () => nextOpenCheckInDay != null && openCheckInModal(nextOpenCheckInDay);
              // Disable until a check-in day's window has actually opened.
              isActionButtonDisabled = nextOpenCheckInDay == null;
            } else {
              actionButtonTitle = t('dashboard.my-progress.uploadPostAssessment');
              onActionButtonClick = () => {
                setResultInitialTab('weightOut');
                toggleEditModal(Sections.Result);
              };
              // Disable until the post-assessment time window opens, matching the
              // form's read-only gate (and the timeline's "unlocked" state). Avoids
              // an active button that only opens a fully read-only modal.
              isActionButtonDisabled = !postAssessmentWindowOpen;
            }

            return (
              <div className={styles['my-progress-action']}>
                <Button
                  title={actionButtonTitle}
                  type={ButtonTypes.BUTTON}
                  theme={Theme.GREEN}
                  buttonStyle={ButtonStyles.SOLID}
                  size={ButtonSize.MEDIUM}
                  onClick={onActionButtonClick}
                  disabled={isActionButtonDisabled}
                />
              </div>
            );
          })()}
        {checkInModal}
      </div>
    </Card>
  );

  return (
    <div className={styles['dashboard-container']}>
      <div className={styles['dashboard-container-layout']}>
        <div className={styles['dashboard-container-layout-left']}>{userDetail}</div>
        {REACT_APP_COUNTRY === 'MY' && isParticipant && <div className={styles['dashboard-container-layout-right']}>{myProgressSection}</div>}
        {REACT_APP_COUNTRY !== 'MY' &&isParticipant && <div className={styles['dashboard-container-layout-right']}>{participantProgress}</div>}
        {REACT_APP_COUNTRY !== 'MY' &&data?.activities && data?.activities?.[0]?.reward?.length > 0 && (
          <div className={styles['dashboard-container-layout-reward']}>{rewardDetail}</div>
        )}
        {isParticipant && <div className={styles['dashboard-container-layout-right']}>
            {assessmentSection}
          </div>
        }
      </div>
      {/* <div className={styles['dashboard-container-layout-bottom']}>
        {isParticipant && !isCoach && !isLeader && <>{teamDetail}</>}
      </div> */}
      {showModal}
      {/* {confirmationPrompt} */}
      {/* {coachSelectionPrompt} */}
    </div>
  );
};
